import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { resolveCharacterArtUrl, resolveMonsterArtUrl } from '../../services/ArtworkResolver';
import { extractCanonicalKey, canonicalKeyToUrl } from '../../utils/pathNormalizer';
import { resolveUploadAssetUrl } from '../../utils/uploadAssetUrlResolver';
import { v4 as uuidv4 } from 'uuid';
import SessionManager from '../../services/SessionManager';
import UnifiedDataManager from '../../services/UnifiedDataManager';
import CombatManager from '../../utils/CombatManager';
import CombatLogManager from '../../utils/CombatLogManager';
import ActionEconomyManager from '../../utils/ActionEconomyManager';
import {
  buildReadableTokenPlacementSearch,
  findNearestUnoccupiedSpace
} from '../../utils/MovementUtils';
import { useMultiplayerSession } from '../../contexts/WebSocketContext';
import TokenAdapter from '../../services/TokenAdapter'; // date_of_change: 2025-10-18 - Centralize token conversion
import { normalizeBattleLayerId } from '../../utils/battleRoomLayerUtils';
import {
  restoreSnapshotTokenCollection
} from '../../utils/BattleRoomTokenRestore';
import { serializeBattleRoomTokenCollection, serializeRosterAddition } from '../../utils/battleRoom/BattleRoomSnapshotSerializer';
import {
  buildCombatActionCatalog,
  buildCombatActionEconomy
} from '../../utils/combat/CombatActionCatalog';
import {
  buildShortAbilityBlock,
  completeAbilityScores,
  resolveAbilityScores
} from '../../utils/combat/CombatantNormalizer';
import { DEBUG_ENABLED } from '../../utils/DebugLogger';
import { rdpSimplify } from '../../utils/fogPolygonUtils'; // GAP-003: polygon/lasso path simplification — date_of_change: 2026-05-20
import { shouldIgnoreEmptyRemoteStateDuringSeed } from '../../utils/battleRoomSeedProtection';
import { shouldSkipBattleRoomSessionRestore } from '../../utils/battleRoom/BattleRoomSessionLoadPolicy';
import { buildCompactTokenGeometryUpdate } from '../../utils/battleRoomTokenGeometry';
import { buildActionEconomySpendRequests } from '../../utils/battleRoom/ActionEconomyPersistence';
import {
  createAuthoritativeRevisionBarrier,
  reconcileAuthoritativeTokenSnapshot
} from '../../utils/battleRoom/AuthoritativeRevisionBarrier';

// date_of_change: 2025-08-29
// REPLACED fragmented imports with UnifiedDataManager:
// - MonsterDataValidator → removed (UnifiedDataManager handles validation)
// - MonsterDataNormalizer → removed (no longer needed)
// - CharacterDataNormalizer → removed (no longer needed)
// - HPFormatBridge → removed (UnifiedDataManager uses schema format directly)
// - DebugLogger → removed (simplified logging)

const debugLog = (...args) => {
  if (DEBUG_ENABLED) {
    console.debug(...args);
  }
};

/**
 * useBattleRoom Hook - Core State Management for Virtual Tabletop
 * 
 * This is the main brain of the virtual tabletop system. It manages all the data and logic
 * that makes the BattleRoom work, including:
 * 
 * WHAT IT MANAGES:
 * - All tokens (characters, monsters, NPCs) on the battle map
 * - Background maps and their positions/sizes
 * - Grid settings (visibility, size, snapping)
 * - Zoom level and camera position
 * - Layer system (which layer is active, what's visible/locked)
 * - Selected tokens and their properties
 * 
 * KEY FUNCTIONS PROVIDED:
 * - File upload handlers for maps and tokens
 * - Drag and drop logic for moving tokens around
 * - Token selection and deselection
 * - Grid snapping calculations
 * - Session save/load (export/import battle states as JSON files)
 * - Asset deletion and management
 * 
 * HOW IT WORKS:
 * - Uses React useState hooks to store all the battle data
 * - Provides callback functions that components can use to modify the data
 * - Handles all the complex logic so components can focus on displaying UI
 * - Automatically updates all connected components when data changes
 * 
 * INTEGRATION:
 * - Used by BattleRoom.js as the main data source
 * - Receives canonical entities from upstream components that load via UnifiedDataManager/EntityStorage
 * - Supports real-time updates across all UI panels
 * CRITICAL SUCCESS FACTORS:
 * - Grid snapping uses Math.round(position/gridSize) * gridSize
 * - Layer order: MapArea -> Grid -> Tokens (maintained in BattleRoom.js)
 * - Zoom affects both Stage scale and grid calculations
 * - All handlers return proper values for component integration
 * - Layer-based interaction restrictions enforce proper asset behavior
 * 
 * STATE VARIABLES:
 * - tokens: Array of token objects with id, x, y, width, height, image, layer
 * - selectedTokenId: Currently selected token ID for interaction
 * - mapImage: Background map image data URL
 * - mapState: Interactive map position, size, and selection state
 * - mapImages: Layer-aware map images { layerId: imageDataUrl }
 * - showGrid/snapToGrid/gridSize/zoom: Grid and zoom configuration
 * - activeLayer/layers: Layer management and configuration
 * 
 * HANDLERS EXPORTED:
 * - handleMapUpload/handleTokenUpload: Asset upload with layer assignment
 * - handleDragEnd/handleDrop: Drag and drop functionality
 * - handleGridToggle/handleSnapToggle/handleGridSizeChange: Grid controls
 * - handleZoomIn/handleZoomOut/handleZoomReset: Zoom controls
 * - handleSaveSession/handleLoadSession: Complete session persistence
 * - handleMapSelect/handleMapChange/handleMapDragEnd: Map interaction
 * - handleDeselectAll/handleTokenSelect: Selection management
 */

const normalizePersistedMapUrl = (value) => {
  if (!value || typeof value !== 'string') return value;
  const resolved = resolveUploadAssetUrl(value);
  if (resolved && typeof resolved === 'string') return resolved;
  const canonicalKey = extractCanonicalKey(value);
  return canonicalKey ? (canonicalKeyToUrl(canonicalKey) || value) : value;
};

const normalizePersistedMapState = (state) => {
  if (!state || typeof state !== 'object') return state;
  return {
    ...state,
    imageUrl: normalizePersistedMapUrl(state.imageUrl)
  };
};

const normalizePersistedMapImages = (images) => {
  if (!images || typeof images !== 'object') return images;
  return Object.entries(images).reduce((normalizedImages, [layerId, value]) => {
    normalizedImages[normalizeBattleLayerId(layerId, layerId)] = normalizePersistedMapUrl(value);
    return normalizedImages;
  }, {});
};

const normalizeBattleRoomGridSize = (value, defaultValue = 50) => {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed)) {
    return defaultValue;
  }
  return Math.min(Math.max(parsed, 20), 100);
};

const normalizeBattleRoomZoom = (value, defaultValue = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return defaultValue;
  }
  return Math.min(Math.max(parsed, 0.05), 6);
};

const buildBattleRoomSessionLoadSignature = (sessionData) => {
  if (!sessionData || typeof sessionData !== 'object') {
    return 'invalid-session-data';
  }

  const battleState = sessionData.battleState || sessionData.data?.battleState || {};
  const combatState = sessionData.combatState || sessionData.data?.combatState || {};
  const tokenList = Array.isArray(battleState.tokens) ? battleState.tokens : [];
  const tokenIds = tokenList.map(token => token?.id || token?.name || 'unknown-token');

  return JSON.stringify({
    version: sessionData.version || 'legacy',
    timestamp: sessionData.timestamp || sessionData.savedAt || null,
    tokenCount: tokenList.length,
    tokenIds,
    mapImage: battleState.mapImage || battleState.mapImages?.map || null,
    combatStarted: !!combatState.combatStarted,
    currentCombatantId: combatState.currentCombatantId || null
  });
};

const getEffectiveBattleGridBounds = ({ mapSize, gridSize, gridWidth, gridHeight }) => {
  const safeGridSize = Math.max(Number(gridSize) || 0, 1);
  const safeGridWidth = Math.max(parseInt(gridWidth, 10) || 0, 0);
  const safeGridHeight = Math.max(parseInt(gridHeight, 10) || 0, 0);
  const safeMapWidth = Math.max(Number(mapSize?.width) || 0, 0);
  const safeMapHeight = Math.max(Number(mapSize?.height) || 0, 0);
  const columns = Math.max(safeGridWidth, Math.ceil(safeMapWidth / safeGridSize) || 0);
  const rows = Math.max(safeGridHeight, Math.ceil(safeMapHeight / safeGridSize) || 0);

  return {
    columns,
    rows,
    pixelWidth: columns * safeGridSize,
    pixelHeight: rows * safeGridSize
  };
};

const useBattleRoom = () => {
  // WebSocket multiplayer integration
  // date_of_change: 2025-09-24 - Added real-time multiplayer support
  const {
    currentSessionId,
    activeSceneId,
    sessionParticipants,
    sessionStatus,
    isInSession,
    isConnected,
    joinSession,
    leaveSession,
    combatState: webSocketCombatState,
    lastUpdate,
    sendCombatAction,
    sendInitiativeUpdate,
    sendTokenUpdate,
    sendMapUpdate,
    sendBattleRoomMutation,
    waitForBattleRoomMutationQueue,
    // Add currentUser for multiplayer functionality
    currentUser,
    // @date_of_change: 2025-09-26 - Added WebSocket-based role information
    userRole,
    isGameMaster: webSocketIsGameMaster
  } = useMultiplayerSession();

  // Multiplayer state management
  const [isMultiplayerMode, setIsMultiplayerMode] = useState(false);
  const [multiplayerSessionId, setMultiplayerSessionId] = useState(null);
  const lastSyncTimestamp = useRef(0);
  const pendingUpdates = useRef(new Map()); // Track pending local updates to avoid conflicts
  const pendingTokenStateSyncRef = useRef(null);
  const lastLoadedSessionSignatureRef = useRef(null);
  const initialSessionSeedRef = useRef({ sessionId: null, expiresAt: 0 });

  // Token ownership and control system
  // date_of_change: 2025-09-24 - Added token ownership/control for multiplayer
  const [tokenOwnership, setTokenOwnership] = useState({}); // { tokenId: { controllerId: userId, controllerType: 'player|gm|all', assignedBy: gmUserId } }
  const lastOwnershipRef = useRef({});
  const [connectedPlayers, setConnectedPlayers] = useState([]); // List of connected players with presence info

  // @date_of_change: 2025-09-26 - Use WebSocket as single source of truth for role (no heuristics)
  // Only trust the WebSocket context - never guess GM status from multiplayer mode
  // CRITICAL: Default to false - never assume GM without explicit server assignment
  const isGameMaster = webSocketIsGameMaster || false;

  // Core state management
  const [tokens, setTokens] = useState([]); // Array of token objects with id, x, y, width, height, image
  const tokensRef = useRef(tokens);
  const tokenCreationQueueRef = useRef(Promise.resolve());
  const rosterScopeRef = useRef(null);
  const rosterMountedRef = useRef(true);
  rosterScopeRef.current = { sessionId: currentSessionId, sceneId: activeSceneId, multiplayer: isMultiplayerMode };
  const [revisionAwaitingTokenCommit, setRevisionAwaitingTokenCommit] = useState(null);
  const [pendingAuthoritativeProjectionCount, setPendingAuthoritativeProjectionCount] = useState(0);
  const authoritativeRevisionBarrierRef = useRef(null);
  if (!authoritativeRevisionBarrierRef.current) {
    authoritativeRevisionBarrierRef.current = createAuthoritativeRevisionBarrier({
      onPendingChange: setPendingAuthoritativeProjectionCount
    });
  }
  const [selectedTokenId, setSelectedTokenId] = useState(null); // Currently selected token ID
  const [mapImage, setMapImage] = useState(null); // Background map image data URL

  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  useEffect(() => {
    if (!Number.isSafeInteger(revisionAwaitingTokenCommit)) return;
    authoritativeRevisionBarrierRef.current.markApplied(revisionAwaitingTokenCommit);
    setRevisionAwaitingTokenCommit(null);
  }, [revisionAwaitingTokenCommit, tokens]);

  useEffect(() => {
    rosterMountedRef.current = true;
    return () => {
      rosterMountedRef.current = false;
      authoritativeRevisionBarrierRef.current?.dispose();
    };
  }, []);

  const waitForAuthoritativeRevision = useCallback((revision) => (
    authoritativeRevisionBarrierRef.current.waitFor(revision)
  ), []);

  // Migrate existing tokens to use hpObj format
  useEffect(() => {
    const migrateExistingTokens = () => {
      const HPMigrationUtility = require('../../utils/HPMigrationUtility').default;
      
      setTokens(currentTokens => {
        const migratedTokens = currentTokens.map(token => {
          // Check if token needs migration (doesn't have hpObj but has hp/maxHp)
          if (!token.hpObj && (token.hp !== undefined || token.maxHp !== undefined)) {
            debugLog(`[useBattleRoom] Migrating token ${token.name} to hpObj format`);
            return HPMigrationUtility.migrateEntity(token);
          }
          return token;
        });
        
        // Only update if there were actual changes
        const hasChanges = migratedTokens.some((token, index) => 
          token !== currentTokens[index]
        );
        
        if (hasChanges) {
          debugLog('[useBattleRoom] Migrated existing tokens to hpObj format');
          return migratedTokens;
        }
        
        return currentTokens;
      });
    };

    // Run migration when tokens change
    if (isLoadingSessionRef.current) {
      // Skip migration while loading a session to avoid cascading updates
      return;
    }
    if (tokens.length > 0) {
      migrateExistingTokens();
    }
  }, [tokens.length]); // Only run when number of tokens changes

  // Map state management for interactive maps
  const [mapState, setMapState] = useState({
    position: { x: 0, y: 0 },
    size: { width: 800, height: 600 },
    isSelected: false
  });
  
  // Grid system state (WORKING IMPLEMENTATION - DO NOT MODIFY WITHOUT TESTING)
  const [showGrid, setShowGrid] = useState(localStorage.getItem('battleRoomShowGrid') === 'false' ? false : true); // Grid visibility toggle
  const [snapToGrid, setSnapToGrid] = useState(localStorage.getItem('battleRoomSnapToGrid') === 'false' ? false : true); // Token snapping toggle
  const [gridSize, setGridSize] = useState(() => normalizeBattleRoomGridSize(localStorage.getItem('battleRoomGridSize'), 50)); // Grid cell size in pixels (20-100 range)
  const normalizeGridDimensionValue = useCallback((value, defaultValue) => {
    const parsed = parseInt(value, 10);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    if (parsed < 10 || parsed > 200) {
      debugLog(`[useBattleRoom] Grid dimension out of range (${parsed}); reverting to default ${defaultValue}`);
      return defaultValue;
    }
    return parsed;
  }, []); // @date_of_change: 2025-11-04 - Memoize grid dimension normalizer for exhaustive-deps

  const getStoredGridDimension = (key, defaultValue) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null || raw === undefined) return defaultValue;
      const normalized = normalizeGridDimensionValue(raw, defaultValue);
      return normalized === null ? defaultValue : normalized;
    } catch (err) {
      debugLog(`[useBattleRoom] Failed to parse ${key}:`, err);
      return defaultValue;
    }
  };

  const [gridOpacity, setGridOpacity] = useState(() => {
    const stored = parseFloat(localStorage.getItem('battleRoomGridOpacity'));
    if (!Number.isFinite(stored)) return 0.5;
    return Math.min(Math.max(stored, 0), 1);
  }); // Grid opacity (0.0-1.0 range)
  // @date_of_change: 2025-11-02 - Defaults updated to 40 columns x 30 rows per UI spec
  const [gridWidth, setGridWidth] = useState(() => getStoredGridDimension('battleRoomGridWidth', 40)); // Grid width in grid units
  const [gridHeight, setGridHeight] = useState(() => getStoredGridDimension('battleRoomGridHeight', 30)); // Grid height in grid units
  const [zoom, setZoom] = useState(() => normalizeBattleRoomZoom(1)); // Canvas zoom level (0.05-6.0 range)
  // @date_of_change: 2025-10-13 - Add gridType/feetPerCell as first-class grid context (engine requires explicit values)
  const [gridType, setGridType] = useState(localStorage.getItem('battleRoomGridType') || 'square');
  const [feetPerCell, setFeetPerCell] = useState(parseInt(localStorage.getItem('battleRoomFeetPerCell'), 10) || 5);
  
  // GAP-002: Drawing layer state — drawings are session-persisted; tool/color/width are UI prefs.
  // date_of_change: 2026-05-20
  const [drawings, setDrawings] = useState([]); // Array of completed drawing objects
  const [drawingEnabled, setDrawingEnabled] = useState(false); // Whether drawing mode is active
  const [drawingTool, setDrawingTool] = useState('freehand'); // Active tool id
  const [drawingColor, setDrawingColor] = useState('#ff0000'); // Stroke color
  const [drawingLineWidth, setDrawingLineWidth] = useState(3); // Stroke width in px

  // Fog of War system state
  // date_of_change: 2025-09-24 - Added fog-of-war support with GM visibility bypass
  const [fogOfWar, setFogOfWar] = useState(() => {
    const enabled = localStorage.getItem('battleRoomFogEnabled');
    const opacity = localStorage.getItem('battleRoomFogOpacity');
    const ignoreGM = localStorage.getItem('battleRoomFogIgnoreGM');
    return {
      enabled: enabled === null ? false : enabled === 'true',
      opacity: opacity ? Math.min(Math.max(parseFloat(opacity) || 0.85, 0.1), 1) : 0.85,
      // Important: operations are not persisted in localStorage; they are session-scoped
      operations: [], // [{ type: 'reveal'|'cover', rectPct: { xPct, yPct, wPct, hPct } }]
      ignoreGM: ignoreGM === null ? true : ignoreGM === 'true'
    };
  });
  
  const stageRef = useRef(null); // Reference to Konva Stage for direct manipulation
  
  // Guard to suppress cascading effects during bulk session load
  const isLoadingSessionRef = useRef(false);

  // Layer-aware map state - support multiple maps on different layers
  const [mapImages, setMapImages] = useState({
    map: null,      // Map layer image
    token: null,    // Token layer background (future feature)
    gamemaster: null // GM layer background (future feature)
  }); // { layerId: imageDataUrl }
  // Presentation-only images are intentionally stored apart from combatant
  // tokens so imported art never acquires fabricated D&D statistics.
  // date_of_change: 2026-09-11
  const [sceneAssets, setSceneAssets] = useState([]);
  
  // Layer management state (will be passed from BattleRoom)
  const [activeLayer, setActiveLayer] = useState('map'); // Start with map layer active for map uploads
  const [layers, setLayers] = useState({
    map: { 
      visible: true, 
      locked: false, 
      mapImage: null, // Store map image directly in layer state
      type: 'map'
    },
    token: { 
      visible: true, 
      locked: false, 
      type: 'token'
    },
    gamemaster: { 
      visible: true, 
      locked: false, 
      type: 'gamemaster'
    }
  });
  
  // Layer-based interaction utilities
  const isLayerInteractive = useCallback((assetLayer) => {
    // Asset is interactive if:
    // 1. Its layer is the currently active layer, AND
    // 2. Its layer is not locked, AND
    // 3. Its layer is visible
    const normalizedLayerId = normalizeBattleLayerId(assetLayer);
    const normalizedActiveLayer = normalizeBattleLayerId(activeLayer, 'map');
    const layer = layers[normalizedLayerId];
    const isActive = normalizedLayerId === normalizedActiveLayer;
    const isVisible = layer ? layer.visible : false;
    const isLocked = layer ? layer.locked : false;

    // Debug log to trace layer interactivity status
    debugLog(`[isLayerInteractive] Checking layer '${normalizedLayerId}':\n- isActive: ${isActive} (activeLayer: '${normalizedActiveLayer}')\n- isVisible: ${isVisible}\n- isLocked: ${isLocked}\n- Draggable: ${isActive && isVisible && !isLocked}`);
    
    return isActive && isVisible && !isLocked;
  }, [activeLayer, layers]);
  
  const getAssetDraggable = useCallback((assetLayer) => {
    return isLayerInteractive(assetLayer);
  }, [isLayerInteractive]);

  // Multiplayer session management
  // date_of_change: 2025-09-24 - Added multiplayer session control
  const enableMultiplayerMode = useCallback(async (sessionId) => {
    try {
      debugLog('[useBattleRoom] Enabling multiplayer mode for session:', sessionId);

      if (sessionId && currentSessionId === sessionId && (sessionStatus === 'joining' || sessionStatus === 'joined')) {
        debugLog('[useBattleRoom] Reusing existing multiplayer session state for target session:', {
          sessionId,
          sessionStatus
        });
        setIsMultiplayerMode(true);
        setMultiplayerSessionId(sessionId);
        return true;
      }

      // date_of_change: 2025-11-30 - Do not fail fast on transient WS disconnects; joinSession handles readiness

      // @date_of_change: 2025-09-26 - Removed HTTP-based GM detection, now using WebSocket-based role detection
      debugLog('[useBattleRoom] enableMultiplayerMode called with:', {
        sessionId,
        currentUserExists: !!currentUser,
        currentUserId: currentUser?.userId,
        webSocketRole: userRole,
        isGameMaster: webSocketIsGameMaster
      });

      await joinSession(sessionId);
      setIsMultiplayerMode(true);
      setMultiplayerSessionId(sessionId);

      debugLog('[useBattleRoom] Multiplayer mode enabled successfully');
      return true;
    } catch (error) {
      console.error('[useBattleRoom] Failed to enable multiplayer mode:', error);
      setIsMultiplayerMode(false);
      setMultiplayerSessionId(null);
      throw error;
    }
  }, [joinSession, currentSessionId, currentUser, sessionStatus, userRole, webSocketIsGameMaster]); // eslint: isConnected removed per exhaustive-deps (only used in debug log)

  const disableMultiplayerMode = useCallback(() => {
    debugLog('[useBattleRoom] Disabling multiplayer mode');

    if (isInSession) {
      leaveSession();
    }

    setIsMultiplayerMode(false);
    setMultiplayerSessionId(null);
    setTokenOwnership({});
    lastOwnershipRef.current = {};
    // @date_of_change: 2025-09-26 - Removed setIsGameMaster - now derived from WebSocket context
    setConnectedPlayers([]);
    lastSyncTimestamp.current = 0;
    pendingUpdates.current.clear();

    debugLog('[useBattleRoom] Multiplayer mode disabled');
  }, [isInSession, leaveSession]);

  // Helper function to build complete map state for backend
  // @date_of_change: 2025-10-03 - Fix WebSocket map update structure and add fog synchronization
  // @date_of_change: 2025-10-05 - Added tokensOverride parameter to fix player lag
  // CRITICAL: buildCompleteMapState must accept fresh tokens to avoid stale closure
  const buildCompleteMapState = useCallback((partialUpdate = {}, tokensOverride = null) => {
    // Use tokensOverride if provided (fresh from handleTokenUpdate), otherwise fall back to closure
    const currentTokens = Array.isArray(partialUpdate?.tokens)
      ? partialUpdate.tokens
      : (tokensOverride !== null ? tokensOverride : tokens);
    const serializedCollection = serializeBattleRoomTokenCollection(currentTokens);
    const incomingTemplates = partialUpdate?.snapshotTemplates || partialUpdate?.characterTemplates || {};

    const state = {
      width: mapState.size.width,
      height: mapState.size.height,
      gridSize: gridSize,
      gridWidth: gridWidth,
      gridHeight: gridHeight,
      // @date_of_change: 2025-10-13 - Include full grid context for AoE/range calculations over WebSocket
      feetPerCell: feetPerCell,
      gridType: gridType,
      // @date_of_change: 2025-10-04 - Include imageUrl for map persistence across refresh
      imageUrl: mapImages.map || mapImage || null,
      // Live rooms use the same deduplicated snapshot contract as saved rooms.
      // Full sheets remain available through snapshotTemplates and are rebuilt
      // client-side, while repeated actors no longer multiply every realtime
      // read and write payload. date_of_change: 2026-08-25
      walls: [],
      doors: [],
      fogOfWar: fogOfWar, // Include fog of war state
      // GAP-002: Include drawings in map state for live sync and persistence
      // date_of_change: 2026-05-25
      drawings: drawings,
      // @date_of_change: 2025-10-11 - Sync ownership and presence so players receive control assignments client-side
      tokenOwnership: tokenOwnership,
      connectedPlayers: connectedPlayers,
      ...partialUpdate,
      tokens: serializedCollection.tokens,
      snapshotVersion: 2,
      snapshotTemplates: {
        ...incomingTemplates,
        ...serializedCollection.templateMap
      }
    };
    debugLog('[buildCompleteMapState] Built state:', {
      tokenCount: state.tokens.length,
      firstToken: state.tokens[0] ? `${state.tokens[0].name} at (${state.tokens[0].x}, ${state.tokens[0].y})` : 'no tokens',
      gridSize: state.gridSize,
      hasImageUrl: !!state.imageUrl,
      fogEnabled: state.fogOfWar?.enabled,
      fogOperations: state.fogOfWar?.operations?.length,
      partialUpdateKeys: Object.keys(partialUpdate),
      usingOverride: tokensOverride !== null
    });
    return state;
  }, [mapState.size.width, mapState.size.height, gridSize, gridWidth, gridHeight, feetPerCell, gridType, mapImages.map, mapImage, tokens, fogOfWar, drawings, tokenOwnership, connectedPlayers]);

  const seedCurrentStateToMultiplayer = useCallback((sessionIdOverride = null) => {
    const targetSessionId = sessionIdOverride || multiplayerSessionId || currentSessionId;
    if (!targetSessionId || !isConnected) {
      debugLog('[useBattleRoom] Skipping live-session seed because socket/session is not ready', {
        targetSessionId,
        isConnected
      });
      return false;
    }

    initialSessionSeedRef.current = {
      sessionId: targetSessionId,
      expiresAt: Date.now() + 8000
    };

    const seededState = buildCompleteMapState({
      seededFromLocalState: true,
      seedTimestamp: Date.now()
    });

    debugLog('[useBattleRoom] Seeding live session with current battle state:', {
      sessionId: targetSessionId,
      tokenCount: seededState.tokens?.length || 0,
      hasMapImage: !!seededState.imageUrl
    });

    const didSend = sendMapUpdate(seededState, targetSessionId);
    if (!didSend) {
      initialSessionSeedRef.current = { sessionId: null, expiresAt: 0 };
    }

    return didSend;
  }, [buildCompleteMapState, currentSessionId, isConnected, multiplayerSessionId, sendMapUpdate]);

  const seedSessionDataToMultiplayer = useCallback((sessionData, sessionIdOverride = null) => {
    const targetSessionId = sessionIdOverride || multiplayerSessionId || currentSessionId;
    if (!targetSessionId || !isConnected || !sessionData || typeof sessionData !== 'object') {
      debugLog('[useBattleRoom] Skipping session-data seed because socket/session/data is not ready', {
        targetSessionId,
        isConnected,
        hasSessionData: !!sessionData
      });
      return false;
    }

    const battleState = sessionData.battleState || sessionData.data?.battleState || sessionData;
    if (!battleState || typeof battleState !== 'object') {
      debugLog('[useBattleRoom] Session-data seed aborted: unrecognized battleState payload');
      return false;
    }

    const snapshotTemplates = (
      sessionData.snapshotTemplates
      || sessionData.characterTemplates
      || sessionData.data?.snapshotTemplates
      || sessionData.data?.characterTemplates
      || {}
    );

    const seededState = {
      width: Number(battleState.mapState?.size?.width) || Number(battleState.mapState?.width) || mapState.size.width,
      height: Number(battleState.mapState?.size?.height) || Number(battleState.mapState?.height) || mapState.size.height,
      gridSize: normalizeBattleRoomGridSize(battleState.gridSize, gridSize),
      gridWidth: normalizeGridDimensionValue(battleState.gridWidth, gridWidth) ?? gridWidth,
      gridHeight: normalizeGridDimensionValue(battleState.gridHeight, gridHeight) ?? gridHeight,
      feetPerCell: Number.isFinite(Number(battleState.feetPerCell)) ? Number(battleState.feetPerCell) : feetPerCell,
      gridType: battleState.gridType || gridType,
      imageUrl: normalizePersistedMapUrl(
        battleState.mapImages?.map
        || battleState.mapImage
        || battleState.mapState?.imageUrl
        || mapImages.map
        || mapImage
        || null
      ),
      tokens: Array.isArray(battleState.tokens) ? battleState.tokens : [],
      fogOfWar: battleState.fogOfWar || fogOfWar,
      tokenOwnership: battleState.tokenOwnership || tokenOwnership,
      connectedPlayers,
      snapshotTemplates,
      seededFromLoadedSession: true,
      seedTimestamp: Date.now()
    };

    initialSessionSeedRef.current = {
      sessionId: targetSessionId,
      expiresAt: Date.now() + 8000
    };

    debugLog('[useBattleRoom] Seeding live session with loaded session data:', {
      sessionId: targetSessionId,
      tokenCount: seededState.tokens.length,
      hasMapImage: !!seededState.imageUrl,
      snapshotTemplateCount: Object.keys(snapshotTemplates).length
    });

    const didSend = sendMapUpdate(seededState, targetSessionId);
    if (!didSend) {
      initialSessionSeedRef.current = { sessionId: null, expiresAt: 0 };
    }

    return didSend;
  }, [
    connectedPlayers,
    currentSessionId,
    feetPerCell,
    fogOfWar,
    gridHeight,
    gridSize,
    gridType,
    gridWidth,
    isConnected,
    mapImage,
    mapImages.map,
    mapState.size.height,
    mapState.size.width,
    multiplayerSessionId,
    normalizeGridDimensionValue,
    sendMapUpdate,
    tokenOwnership
  ]);

  // WebSocket state synchronization
  // date_of_change: 2025-09-24 - Added real-time state sync
  // @date_of_change: 2025-10-05 - Added freshTokens parameter to fix player lag
  const syncStateToMultiplayer = useCallback((updateType, data, freshTokens = null) => {
    if (!isMultiplayerMode || !isInSession) {
      debugLog('[syncStateToMultiplayer] Skipping sync - not ready:', {isMultiplayerMode, isInSession, isConnected});
      return false;
    }

    try {
      const updatePayload = {
        type: updateType,
        data: data,
        timestamp: Date.now(),
        sessionId: multiplayerSessionId
      };

      // Track pending update to avoid conflicts
      const updateId = `${updateType}_${Date.now()}_${Math.random()}`;
      pendingUpdates.current.set(updateId, updatePayload);

      debugLog('[syncStateToMultiplayer] Syncing:', updateType, 'with data:', data, 'freshTokens:', freshTokens ? `${freshTokens.length} tokens` : 'using closure');

      switch (updateType) {
        case 'token_move':
          // @date_of_change: 2025-10-05 - Pass freshTokens to avoid stale closure
          // Send complete map state with updated tokens
          return sendMapUpdate(buildCompleteMapState({
            updateType: updateType,
            updateData: data,
            updateId: updateId
          }, freshTokens));

        case 'token_delete':
          // Deletion needs only membership intent, never every embedded sheet.
          // Canonical roster command; date_of_change: 2026-11-05.
          return sendMapUpdate({ updateType, updateData: { tokenId: data.tokenId }, updateId, tokens: [] });

        case 'token_update': {
          const geometryUpdate = buildCompactTokenGeometryUpdate(data);
          if (geometryUpdate) return sendTokenUpdate(geometryUpdate);
          return sendMapUpdate(buildCompleteMapState({
            updateType: updateType,
            updateData: data,
            updateId: updateId
          }, freshTokens));
        }

        case 'map_update':
          return sendMapUpdate(buildCompleteMapState({
            ...data,
            updateType: updateType,
            updateId: updateId
          }));

        case 'combat_action':
          return sendCombatAction(data);

        case 'token_ownership':
          // @date_of_change: 2025-10-15 - Send full map via updateMap to persist and broadcast ownership reliably
          // Rationale: backend 'combat:update' may reject partial map patches when map is uninitialized (schema requires width/height/gridSize).
          // Sending a complete map state via updateMap avoids TDZ/validation issues and ensures players receive ownership.
          return sendMapUpdate(buildCompleteMapState({
            tokenOwnership: data,
            updateType: updateType,
            updateId: updateId
          }));

        case 'player_presence':
          return sendMapUpdate(buildCompleteMapState({
            connectedPlayers: data,
            updateType: updateType,
            updateId: updateId
          }));

        case 'fog_update':
          // @date_of_change: 2025-10-03 - Added fog of war synchronization
          return sendMapUpdate(buildCompleteMapState({
            fogOfWar: data,
            updateType: updateType,
            updateId: updateId
          }));

        case 'drawing_update':
          // date_of_change: 2026-05-25 - Added drawing layer synchronization
          return sendMapUpdate(buildCompleteMapState({
            drawings: data.drawings,
            updateType: updateType,
            updateId: updateId
          }));

        default:
          debugLog('[useBattleRoom] Unknown update type for multiplayer sync:', updateType);
          return false;
      }
    } catch (error) {
      console.error('[useBattleRoom] Failed to sync state to multiplayer:', error);
      return false;
    }
  // @date_of_change: 2025-11-04 - Removed unused deps (tokens, sendCombatUpdate) per ESLint feedback; buildCompleteMapState already tracks tokens
  }, [isMultiplayerMode, isInSession, isConnected, multiplayerSessionId, sendMapUpdate, sendTokenUpdate, sendCombatAction, buildCompleteMapState]);

  // GAP-002: Drawing layer functions — defined after syncStateToMultiplayer to avoid no-use-before-define
  // date_of_change: 2026-05-25 - Moved here after syncStateToMultiplayer for correct dependency order

  // Add a completed drawing stroke to the session drawings
  // rule_reference: Drawings are stored as serializable objects (type, points/startPoint/endPoint, color, lineWidth)
  const addDrawing = useCallback((drawing) => {
    if (!drawing || typeof drawing !== 'object') return;
    const newDrawing = { ...drawing, id: drawing.id || `draw-${Date.now()}-${Math.random()}` };
    setDrawings(prev => {
      const next = [...prev, newDrawing];
      // Sync to multiplayer like fog does
      if (isMultiplayerMode) {
        syncStateToMultiplayer('drawing_update', { drawings: next, operation: 'add', drawing: newDrawing });
      }
      return next;
    });
  }, [isMultiplayerMode, syncStateToMultiplayer]);

  // Undo the most recent drawing stroke (last item in array)
  const undoDrawing = useCallback(() => {
    setDrawings(prev => {
      if (prev.length === 0) return prev;
      const next = prev.slice(0, -1);
      // Sync to multiplayer like fog does
      if (isMultiplayerMode) {
        syncStateToMultiplayer('drawing_update', { drawings: next, operation: 'undo' });
      }
      return next;
    });
  }, [isMultiplayerMode, syncStateToMultiplayer]);

  // Clear all drawings from the session layer
  const clearDrawings = useCallback(() => {
    setDrawings([]);
    // Sync to multiplayer like fog does
    if (isMultiplayerMode) {
      syncStateToMultiplayer('drawing_update', { drawings: [], operation: 'clear' });
    }
  }, [isMultiplayerMode, syncStateToMultiplayer]);

  // Handle incoming WebSocket updates
  // date_of_change: 2025-09-24 - Added incoming update handler
  useEffect(() => {
    if (!isMultiplayerMode || !webSocketCombatState || !lastUpdate) {
      return;
    }

    // Avoid processing our own updates
    if (lastUpdate <= lastSyncTimestamp.current) {
      return;
    }

    debugLog('[useBattleRoom] Processing incoming WebSocket update:', webSocketCombatState);

    try {
      // Check if this is an update we sent (to avoid infinite loops)
      const updateId = webSocketCombatState.updateId;
      if (updateId && pendingUpdates.current.has(updateId)) {
        // This is our own update coming back - remove from pending and ignore
        pendingUpdates.current.delete(updateId);
        lastSyncTimestamp.current = lastUpdate;
        return;
      }

      const pendingSeed = initialSessionSeedRef.current;
      const pendingSeedActive = pendingSeed.sessionId &&
        pendingSeed.sessionId === (multiplayerSessionId || currentSessionId) &&
        pendingSeed.expiresAt > Date.now();
      const incomingTokens = Array.isArray(webSocketCombatState.tokens) ? webSocketCombatState.tokens : null;
      const incomingSceneExists = !!(
        (incomingTokens && incomingTokens.length > 0) ||
        webSocketCombatState.imageUrl ||
        webSocketCombatState.map?.imageUrl
      );

      if (pendingSeedActive && incomingSceneExists) {
        debugLog('[useBattleRoom] Initial live-session seed acknowledged by remote state');
        initialSessionSeedRef.current = { sessionId: null, expiresAt: 0 };
      }

      // React may not have committed loaded tokens by the time the server's
      // previously empty scene arrives. The explicit seed window is sufficient
      // proof of local promotion intent; requiring `localSceneExists` here lets
      // that empty packet erase a successful save restore. date_of_change: 2026-08-18
      if (shouldIgnoreEmptyRemoteStateDuringSeed({ pendingSeedActive, incomingSceneExists })) {
        debugLog('[useBattleRoom] Ignoring empty remote state while promoting local tabletop to live session');
        lastSyncTimestamp.current = lastUpdate;
        return;
      }

      // Process the incoming update based on type
      // @date_of_change: 2025-10-04 - Backend now uses socket.broadcast to prevent echo
      // No need to filter self-originated updates - we won't receive our own updates back
      if (webSocketCombatState.tokens && Array.isArray(webSocketCombatState.tokens)) {
        debugLog('[useBattleRoom] Updating tokens from WebSocket:', {
          tokenCount: webSocketCombatState.tokens.length,
          firstToken: webSocketCombatState.tokens[0] ?
            `${webSocketCombatState.tokens[0].name} at (${webSocketCombatState.tokens[0].x}, ${webSocketCombatState.tokens[0].y})` :
            'no tokens'
        });
        // @date_of_change: 2025-10-09 - Merge-by-id and prefer locally newer tokens to prevent snapback
        const templateMap = webSocketCombatState.snapshotTemplates
          || webSocketCombatState.characterTemplates
          || webSocketCombatState.map?.snapshotTemplates
          || webSocketCombatState.map?.characterTemplates
          || {};
        const incomingTokens = restoreSnapshotTokenCollection({
          tokens: webSocketCombatState.tokens,
          templateMap,
          canonicalCharacterEntities: UnifiedDataManager.getAllFromLocalStorage('character'),
          canonicalMonsterEntities: UnifiedDataManager.getAllFromLocalStorage('monster')
        });
        const incomingRevision = Number(webSocketCombatState._stateRevision);
        setTokens(prevTokens => {
          try {
            return reconcileAuthoritativeTokenSnapshot({
              previousTokens: prevTokens,
              incomingTokens,
              revision: incomingRevision
            });
          } catch (e) {
            console.error('[useBattleRoom] Rejected unversioned authoritative token snapshot:', e);
            return prevTokens;
          }
        });
        if (Number.isSafeInteger(incomingRevision) && incomingRevision >= 0) {
          setRevisionAwaitingTokenCommit((current) => (
            Number.isSafeInteger(current) ? Math.max(current, incomingRevision) : incomingRevision
          ));
        }
      } else {
        debugLog('[useBattleRoom] No tokens in WebSocket update:', {
          hasTokens: !!webSocketCombatState.tokens,
          isArray: Array.isArray(webSocketCombatState.tokens),
          combatStateKeys: Object.keys(webSocketCombatState)
        });
      }

      const incomingSceneAssets = webSocketCombatState.map?.assets ?? webSocketCombatState.assets;
      if (Array.isArray(incomingSceneAssets)) {
        setSceneAssets(incomingSceneAssets);
      }

      // @date_of_change: 2025-10-04 - Apply map image from combat state on refresh
      if (webSocketCombatState.imageUrl) {
        debugLog('[useBattleRoom] Updating map image from WebSocket');
        const normalizedMapUrl = normalizePersistedMapUrl(webSocketCombatState.imageUrl);
        setMapImage(normalizedMapUrl);
        setMapImages(prev => ({
          ...prev,
          map: normalizedMapUrl
        }));
      }

      if (webSocketCombatState.mapState) {
        debugLog('[useBattleRoom] Updating map state from WebSocket');
        setMapState(normalizePersistedMapState(webSocketCombatState.mapState));
      }

      const incomingMapWidth = Number(webSocketCombatState.width);
      const incomingMapHeight = Number(webSocketCombatState.height);
      if (Number.isFinite(incomingMapWidth) && incomingMapWidth > 0 && Number.isFinite(incomingMapHeight) && incomingMapHeight > 0) {
        debugLog('[useBattleRoom] Updating map dimensions from WebSocket:', incomingMapWidth, 'x', incomingMapHeight);
        setMapState(prev => (
          prev.size?.width === incomingMapWidth && prev.size?.height === incomingMapHeight
            ? prev
            : {
                ...prev,
                size: {
                  ...prev.size,
                  width: incomingMapWidth,
                  height: incomingMapHeight
                }
              }
        ));
      }

      if (webSocketCombatState.tokenOwnership) {
        try {
          const entries = Object.entries(webSocketCombatState.tokenOwnership || {});
          const sample = entries[0] ? { tokenId: entries[0][0], ownership: entries[0][1] } : null;
          debugLog('[useBattleRoom] Updating token ownership from WebSocket', {
            count: entries.length,
            sample,
            currentUserId: currentUser?.userId
          });
        } catch (_) {}
        lastOwnershipRef.current = webSocketCombatState.tokenOwnership;
        setTokenOwnership(webSocketCombatState.tokenOwnership);
      }

      if (webSocketCombatState.gridSize !== undefined) {
        const incomingGridSize = normalizeBattleRoomGridSize(webSocketCombatState.gridSize, 50);
        debugLog('[useBattleRoom] Updating grid size from WebSocket:', incomingGridSize);
        setGridSize(incomingGridSize);
        localStorage.setItem('battleRoomGridSize', String(incomingGridSize));
      }
      if (webSocketCombatState.feetPerCell !== undefined) {
        debugLog('[useBattleRoom] Updating feetPerCell from WebSocket:', webSocketCombatState.feetPerCell);
        const fpc = Number(webSocketCombatState.feetPerCell);
        if (Number.isFinite(fpc)) {
          setFeetPerCell(fpc);
          localStorage.setItem('battleRoomFeetPerCell', String(fpc));
        }
      }
      if (webSocketCombatState.gridType) {
        debugLog('[useBattleRoom] Updating gridType from WebSocket:', webSocketCombatState.gridType);
        setGridType(webSocketCombatState.gridType);
        localStorage.setItem('battleRoomGridType', String(webSocketCombatState.gridType));
      }

      const incomingGridWidth = normalizeGridDimensionValue(
        webSocketCombatState.gridWidth ?? webSocketCombatState.map?.gridWidth,
        40
      );
      if (incomingGridWidth !== null) {
        debugLog('[useBattleRoom] Updating grid width from WebSocket:', incomingGridWidth);
        setGridWidth(incomingGridWidth);
        localStorage.setItem('battleRoomGridWidth', String(incomingGridWidth));
      }

      const incomingGridHeight = normalizeGridDimensionValue(
        webSocketCombatState.gridHeight ?? webSocketCombatState.map?.gridHeight,
        30
      );
      if (incomingGridHeight !== null) {
        debugLog('[useBattleRoom] Updating grid height from WebSocket:', incomingGridHeight);
        setGridHeight(incomingGridHeight);
        localStorage.setItem('battleRoomGridHeight', String(incomingGridHeight));
      }

      if (webSocketCombatState.fogOfWar) {
        debugLog('[useBattleRoom] Updating fog of war from WebSocket:', {
          enabled: webSocketCombatState.fogOfWar.enabled,
          operations: webSocketCombatState.fogOfWar.operations?.length || 0,
          operationsDetail: webSocketCombatState.fogOfWar.operations,
          opacity: webSocketCombatState.fogOfWar.opacity,
          ignoreGM: webSocketCombatState.fogOfWar.ignoreGM
        });
        // Merge incoming fog state, preserving existing operations if the incoming payload omits them.
        setFogOfWar(prev => {
          const incoming = webSocketCombatState.fogOfWar || {};
          const nextOps = Array.isArray(incoming.operations) ? incoming.operations : (prev?.operations || []);
          return {
            ...prev,
            ...incoming,
            operations: nextOps
          };
        });
      }

      if (webSocketCombatState.connectedPlayers) {
        debugLog('[useBattleRoom] Updating connected players from WebSocket');
        setConnectedPlayers(webSocketCombatState.connectedPlayers);
      }

      // Update timestamp to prevent reprocessing
      lastSyncTimestamp.current = lastUpdate;

    } catch (error) {
      debugLog('[useBattleRoom] Error processing WebSocket update:', error);
    }
  }, [isMultiplayerMode, webSocketCombatState, lastUpdate, currentSessionId, currentUser?.userId, mapImage, mapImages.map, multiplayerSessionId, normalizeGridDimensionValue, tokens.length]);

  // Token ownership and permission system
  // date_of_change: 2025-09-24 - Added token control management for multiplayer (moved for hoisting)

  /**
   * Check if the current user can control a specific token
   * @param {string} tokenId - Token ID to check
   * @returns {boolean} - Whether the user can control this token
   */
  const canControlToken = useCallback((tokenId) => {
    try {
      const ownership = tokenOwnership[tokenId] || lastOwnershipRef.current?.[tokenId];
      const currentUserId = currentUser?.userId;
      debugLog('[canControlToken] check', { tokenId, isMultiplayerMode, isGameMaster, currentUserId, ownership });

      if (!isMultiplayerMode) {
        debugLog('[canControlToken] single-player -> true');
        return true;
      }

      if (isGameMaster) {
        debugLog('[canControlToken] gm -> true');
        return true;
      }

      if (!ownership) {
        debugLog('[canControlToken] no ownership -> false');
        return false;
      }

      switch (ownership.controllerType) {
        case 'all': {
          debugLog('[canControlToken] controllerType=all -> true');
          return true;
        }
        case 'player': {
          const allow = ownership.controllerId === currentUserId;
          debugLog('[canControlToken] controllerType=player', { controllerId: ownership.controllerId, currentUserId, allow });
          return allow;
        }
        case 'gm': {
        debugLog('[canControlToken] controllerType=gm -> false');
          return false;
        }
        default: {
          debugLog('[canControlToken] controllerType=unknown -> false');
          return false;
        }
      }
    } catch (e) {
      debugLog('[canControlToken] error', e);
      return false;
    }
  }, [isMultiplayerMode, isGameMaster, tokenOwnership, currentUser]);

  // Combat action synchronization event listeners with 1s delay tolerance
  // date_of_change: 2025-09-24 - Added real-time combat action sync
  useEffect(() => {
    if (!isMultiplayerMode) return;

    const handleCombatAction = (event) => {
      const { actionData, isRemoteAction } = event.detail;
      debugLog('[useBattleRoom] Received combat action:', actionData, 'isRemote:', isRemoteAction);

      // Only process remote actions to avoid duplicate processing of local actions
      if (isRemoteAction) {
        // Apply remote combat action
        // This handles attack rolls, movement, spell casting, etc.
        if (actionData.rollResults) {
          debugLog('[useBattleRoom] Applying remote combat results:', actionData.rollResults);

          // Update combat log through CombatLogManager if available
          if (actionData.rollResults.damage && actionData.rollResults.damage > 0) {
            const combatLogEntry = {
              timestamp: new Date().toISOString(),
              actor: actionData.action?.name || 'Remote Player',
              action: actionData.action?.type || 'attack',
              target: actionData.targets?.[0]?.name || 'Unknown Target',
              result: `${actionData.rollResults.damage} damage`,
              details: actionData.rollResults
            };
            CombatLogManager.logEntry(combatLogEntry);
          }
        }

        // Apply target HP changes if present
        if (actionData.targets && actionData.targets.length > 0) {
          setTokens(prevTokens => {
            return prevTokens.map(token => {
              const targetUpdate = actionData.targets.find(t => t.id === token.id);
              if (targetUpdate && targetUpdate.hpChange) {
                const currentHp = token.hpObj?.current || token.hp || 100;
                const newHp = Math.max(0, currentHp + targetUpdate.hpChange);

                debugLog('[useBattleRoom] Applying HP change to token:', {
                  tokenId: token.id,
                  oldHp: currentHp,
                  hpChange: targetUpdate.hpChange,
                  newHp
                });

                return {
                  ...token,
                  hp: newHp,
                  hpObj: {
                    ...token.hpObj,
                    current: newHp,
                    max: token.hpObj?.max || token.maxHp || 100
                  }
                };
              }
              return token;
            });
          });
        }
      }
    };

    const handleInitiativeUpdate = (event) => {
      const { initiativeData, isRemoteUpdate } = event.detail;
      debugLog('[useBattleRoom] Received initiative update:', initiativeData, 'isRemote:', isRemoteUpdate);

      // Only apply remote initiative updates to avoid conflicts
      if (isRemoteUpdate) {
        // Update initiative order, current turn, and round from remote GM/player
        if (initiativeData.combatants && Array.isArray(initiativeData.combatants)) {
          setTokens(prevTokens => {
            return prevTokens.map(token => {
              const updatedCombatant = initiativeData.combatants.find(c => c.id === token.id);
              if (updatedCombatant && updatedCombatant.initiative !== undefined) {
                return {
                  ...token,
                  initiative: updatedCombatant.initiative,
                  ...(initiativeData.authoritative ? {
                    initiativeRoll: updatedCombatant.initiativeRoll,
                    initiativeModifier: updatedCombatant.initiativeRoll?.modifier
                  } : {})
                };
              }
              return token;
            });
          });
        }
      }
    };

    const handleTokenUpdate = (event) => {
      const { tokenData, isRemoteUpdate } = event.detail;
      debugLog('[useBattleRoom] Received token update:', tokenData, 'isRemote:', isRemoteUpdate);

      // Apply remote token updates (position, HP, properties)
      if (isRemoteUpdate && tokenData.tokenData) {
        const tokenId = tokenData.tokenId || tokenData.tokenData.id;
        const updateType = tokenData.updateType || 'full';
        const isGMOverride = tokenData.isGMOverride || false;

        debugLog('[useBattleRoom] Applying token update:', {
          tokenId,
          updateType,
          isGMOverride
        });

        // Apply the update using the existing handleTokenUpdate mechanism
        // GM overrides should always be applied regardless of current user permissions
        if (isGMOverride || canControlToken(tokenId)) {
          setTokens(prevTokens => {
            return prevTokens.map(token => {
              if (token.id === tokenId) {
                // @date_of_change: 2025-10-05 - Bridge HP updates via tokenUpdate(updateType:'hp') until combat:hp is implemented
                if (updateType === 'hp') {
                  const incoming = tokenData.tokenData || {};
                  const currentHp = token.hpObj?.current ?? token.hp ?? 0;
                  const maxHp = token.hpObj?.max ?? token.maxHp ?? Math.max(currentHp, 0);
                  const delta = (incoming.hpDelta !== undefined ? incoming.hpDelta : (incoming.hpChange !== undefined ? incoming.hpChange : null));
                  const absolute = (incoming.hp !== undefined ? incoming.hp : null);
                  let newHp = currentHp;
                  if (absolute !== null) {
                    newHp = Number(absolute) || 0;
                  } else if (delta !== null) {
                    newHp = currentHp + (Number(delta) || 0);
                  }
                  newHp = Math.max(0, Math.min(maxHp, newHp));

                  // Temp HP handling (optional fields)
                  const currentTemp = token.hpObj?.temp ?? token.tempHp ?? 0;
                  let newTemp = currentTemp;
                  if (incoming.tempHp !== undefined) {
                    newTemp = Number(incoming.tempHp) || 0;
                  } else if (incoming.tempHpDelta !== undefined) {
                    newTemp = currentTemp + (Number(incoming.tempHpDelta) || 0);
                  }
                  newTemp = Math.max(0, newTemp);

                  return {
                    ...token,
                    hp: newHp,
                    tempHp: newTemp,
                    hpObj: {
                      ...(token.hpObj || {}),
                      current: newHp,
                      max: maxHp,
                      temp: newTemp
                    },
                    lastModified: incoming.lastModified,
                    modifiedBy: incoming.modifiedBy
                  };
                }

                // Default merge for other update types
                return {
                  ...token,
                  ...tokenData.tokenData,
                  lastModified: tokenData.tokenData.lastModified,
                  modifiedBy: tokenData.tokenData.modifiedBy
                };
              }
              return token;
            });
          });
        }
      }
    };

    // Register event listeners for combat action synchronization
    window.addEventListener('websocket-combat-action', handleCombatAction);
    window.addEventListener('websocket-initiative-update', handleInitiativeUpdate);
    window.addEventListener('websocket-token-update', handleTokenUpdate);

    return () => {
      window.removeEventListener('websocket-combat-action', handleCombatAction);
      window.removeEventListener('websocket-initiative-update', handleInitiativeUpdate);
      window.removeEventListener('websocket-token-update', handleTokenUpdate);
    };
  }, [isMultiplayerMode, canControlToken, setTokens]);

  // Token ownership and permission system moved above for hoisting
  // date_of_change: 2025-09-24 - Added token control management for multiplayer

  /**
   * Assign token control to a player or group
   * @param {string} tokenId - Token ID to assign
   * @param {string} controllerType - 'player', 'gm', 'all'
   * @param {string} controllerId - Specific user ID (for 'player' type)
   */
  const assignTokenControl = useCallback((tokenId, controllerType, controllerId = null) => {
    if (!isGameMaster) {
      debugLog('[useBattleRoom] Only GM can assign token control');
      return false;
    }

    const newOwnership = {
      ...tokenOwnership,
      [tokenId]: {
        controllerId: controllerId,
        controllerType: controllerType,
        assignedBy: currentUser?.userId,
        assignedAt: Date.now()
      }
    };

    setTokenOwnership(newOwnership);
    lastOwnershipRef.current = newOwnership;

    // Sync to multiplayer session
    if (isMultiplayerMode) {
      syncStateToMultiplayer('token_ownership', newOwnership);
    }

    debugLog(`[useBattleRoom] Token ${tokenId} assigned to ${controllerType}${controllerId ? ` (${controllerId})` : ''}`);
    return true;
  }, [isGameMaster, tokenOwnership, currentUser, isMultiplayerMode, syncStateToMultiplayer]);

  /**
   * Remove token control assignment (GM only)
   * @param {string} tokenId - Token ID to unassign
   */
  const unassignTokenControl = useCallback((tokenId) => {
    if (!isGameMaster) {
      debugLog('[useBattleRoom] Only GM can unassign token control');
      return false;
    }

    const newOwnership = { ...tokenOwnership };
    delete newOwnership[tokenId];

    setTokenOwnership(newOwnership);

    // Sync to multiplayer session
    if (isMultiplayerMode) {
      syncStateToMultiplayer('token_ownership', newOwnership);
    }

    debugLog(`[useBattleRoom] Token ${tokenId} control unassigned`);
    return true;
  }, [isGameMaster, tokenOwnership, isMultiplayerMode, syncStateToMultiplayer]);

  /**
   * Get user-friendly display info for token ownership
   * @param {string} tokenId - Token ID to check
   * @returns {Object} - Display information about token control
   */
  const getTokenControlInfo = useCallback((tokenId) => {
    const ownershipSource = tokenOwnership[tokenId] || lastOwnershipRef.current?.[tokenId];
    const ownership = ownershipSource;
    if (!ownership) {
      return { controlled: false, displayText: 'Unassigned', color: '#gray' };
    }

    switch (ownership.controllerType) {
      case 'all':
        return { controlled: true, displayText: 'All Players', color: '#22c55e' };
      case 'gm':
        return { controlled: true, displayText: 'Game Master', color: '#ef4444' };
      case 'player':
        const player = connectedPlayers.find(p => p.userId === ownership.controllerId);
        const playerName = player?.displayName || 'Unknown Player';
        return {
          controlled: true,
          displayText: playerName,
          color: player?.presenceColor || '#3b82f6',
          userId: ownership.controllerId
        };
      default:
        return { controlled: false, displayText: 'Unassigned', color: '#gray' };
    }
  }, [tokenOwnership, connectedPlayers]);

  // Map interaction handlers
  const handleMapSelect = useCallback(() => {
    if (isLayerInteractive('map')) {
      setMapState(prev => ({ ...prev, isSelected: true }));
      setSelectedTokenId(null); // Deselect tokens when selecting map
    }
  }, [isLayerInteractive]);

  const handleMapChange = useCallback((newMapAttrs) => {
    const newMapState = {
      position: { x: newMapAttrs.x, y: newMapAttrs.y },
      size: { width: newMapAttrs.width, height: newMapAttrs.height }
    };

    setMapState(prev => ({
      ...prev,
      ...newMapState
    }));

    // Sync to multiplayer session if active
    // date_of_change: 2025-09-24 - Added multiplayer state sync
    if (isMultiplayerMode) {
      syncStateToMultiplayer('map_update', newMapState);
    }
  }, [isMultiplayerMode, syncStateToMultiplayer]);

  // date_of_change: 2025-08-31
  // Moved handleTokenUpdate above its consumers (e.g., handleExecuteAttack) to avoid TDZ errors
  const handleTokenUpdate = useCallback((tokenId, newProperties) => {
    debugLog('[useBattleRoom] handleTokenUpdate called with:', {
      tokenId,
      newProperties,
      currentTokensCount: tokens.length
    });

    // Check permissions for multiplayer token updates
    // date_of_change: 2025-09-24 - Added permission checks with GM override
    if (isMultiplayerMode) {
      const hasPermission = canControlToken(tokenId);
      const isGMOverride = isGameMaster && newProperties._gmOverride;
      // date_of_change: 2025-11-12 — Allow engine-driven state writes (HP/status from rules engine)
      // rule_reference: AGENTS.md (No fallbacks; respect authoritative engine outcomes.)
      // Engine-originated updates use `_engineUpdate: true` and bypass per-token control gating safely.
      const isEngineUpdate = newProperties._engineUpdate === true;

      if (!hasPermission && !isGMOverride && !isEngineUpdate) {
        debugLog(`[handleTokenUpdate] User does not have permission to update token ${tokenId}`);
        return false;
      }

      // Annotate origin and strip override flags before persistence
      if (isGMOverride) {
        debugLog(`[handleTokenUpdate] GM override for token ${tokenId}`);
        newProperties = { ...newProperties, _lastModifiedBy: 'gm', _lastModified: Date.now() };
        delete newProperties._gmOverride;
      } else if (isEngineUpdate) {
        newProperties = { ...newProperties, _lastModifiedBy: 'engine', _lastModified: Date.now() };
        delete newProperties._engineUpdate;
      } else if (hasPermission) {
        newProperties = { ...newProperties, _lastModifiedBy: currentUser?.userId, _lastModified: Date.now() };
      }
    }
    
    // 🔍 PHASE 3: Token Update Data Validation
    debugLog('[handleTokenUpdate] Token update receiving:', {
      tokenId,
      newProperties,
      hasActions: !!newProperties.actions,
      actionsCount: newProperties.actions?.length || 0,
      updateKeys: Object.keys(newProperties),
      hasMonsterData: !!newProperties.monsterData
    }); 
    // date_of_change: 2025-08-29 - No longer need normalizers, data already consistent

    // date_of_change: 2025-10-27 - Functional state update to avoid stale-token overwrites during multi-target spells.
    setTokens(prevTokens => {
      const tokenIndex = prevTokens.findIndex(t => t.id === tokenId);
      if (tokenIndex === -1) {
        debugLog(`[handleTokenUpdate] Token with id ${tokenId} not found`);
        return prevTokens;
      }

      const currentToken = prevTokens[tokenIndex];
      const isMonster = currentToken.type === 'monster';

      debugLog(`[handleTokenUpdate] Starting update for token ${tokenId} with properties:`, newProperties);

      const cloneStatusPayload = (value, fallback = {}) => {
        if (!value || typeof value !== 'object') return { ...(fallback || {}) };
        try {
          return JSON.parse(JSON.stringify(value));
        } catch (err) {
          return Array.isArray(value) ? [...value] : { ...value };
        }
      };

      const statusEffectsProvided = Object.prototype.hasOwnProperty.call(newProperties, 'statusEffects');
      const statusEffectsMetaProvided = Object.prototype.hasOwnProperty.call(newProperties, 'statusEffectsMeta');
      const nextStatusEffects = statusEffectsProvided
        ? cloneStatusPayload(newProperties.statusEffects, {})
        : cloneStatusPayload(currentToken.statusEffects, {});
      const nextStatusEffectsMeta = statusEffectsMetaProvided
        ? cloneStatusPayload(newProperties.statusEffectsMeta, {})
        : cloneStatusPayload(currentToken.statusEffectsMeta, {});

      let processedProperties = { ...newProperties };
      if (statusEffectsProvided) {
        processedProperties.statusEffects = nextStatusEffects;
      }
      if (statusEffectsMetaProvided) {
        processedProperties.statusEffectsMeta = nextStatusEffectsMeta;
      }

      if (
        newProperties.abilities ||
        newProperties.strength || newProperties.dexterity ||
        newProperties.constitution || newProperties.intelligence ||
        newProperties.wisdom || newProperties.charisma
      ) {
        const currentAbilities = resolveAbilityScores(
          currentToken.abilities,
          currentToken.stats,
          currentToken
        );
        const incomingAbilities = resolveAbilityScores(
          newProperties.abilities,
          newProperties.stats,
          newProperties
        );
        const abilities = completeAbilityScores(
          {
            ...currentAbilities,
            ...incomingAbilities
          },
          10
        );

        processedProperties.abilities = abilities;
        processedProperties.strength = abilities.strength;
        processedProperties.dexterity = abilities.dexterity;
        processedProperties.constitution = abilities.constitution;
        processedProperties.intelligence = abilities.intelligence;
        processedProperties.wisdom = abilities.wisdom;
        processedProperties.charisma = abilities.charisma;
        processedProperties.stats = buildShortAbilityBlock(abilities, 10);

        const dexMod = Math.floor((abilities.dexterity - 10) / 2);
        if (!newProperties.hasOwnProperty('initiative')) {
          processedProperties.initiative = dexMod;
        }
      }

      if (newProperties.hp || newProperties.hpObj || newProperties['hp.current'] || newProperties['hp.max']) {
        const HPMigrationUtility = require('../../utils/HPMigrationUtility').default;
        const tempToken = { ...currentToken, ...newProperties };

        if (newProperties['hp.current'] !== undefined) {
          if (!tempToken.hp) tempToken.hp = {};
          tempToken.hp.current = newProperties['hp.current'];
        }
        if (newProperties['hp.max'] !== undefined) {
          if (!tempToken.hp) tempToken.hp = {};
          tempToken.hp.max = newProperties['hp.max'];
        }

        const migratedToken = HPMigrationUtility.migrateEntity(tempToken);
        processedProperties.hpObj = migratedToken.hpObj;
        processedProperties.hp = migratedToken.hp;
        processedProperties.maxHp = migratedToken.maxHp;

        debugLog('[handleTokenUpdate] HP data processed:', {
          hpObj: processedProperties.hpObj,
          hp: processedProperties.hp,
          maxHp: processedProperties.maxHp
        });
      }

      let updatedToken = {
        ...currentToken,
        ...processedProperties
      };

      debugLog('[handleTokenUpdate] Pre-normalization token state:', {
        id: updatedToken.id,
        name: updatedToken.name,
        type: updatedToken.type,
        hp: updatedToken.hp,
        abilities: updatedToken.abilities,
        actions: updatedToken.actions?.length
      });

      const isMajorUpdate = ['abilities', 'actions', 'traits', 'features', 'spells', 'equipment', 'hp', 'artwork', 'image', 'token'].some(
        key => newProperties.hasOwnProperty(key)
      );

      if (newProperties.actions) {
        const processedActions = Array.isArray(newProperties.actions)
          ? newProperties.actions.map(action => ({
              ...action,
              name: action.name || 'Unnamed Action',
              description: action.description || action.desc || action.text || '',
              desc: action.description || action.desc || action.text || '',
              text: action.description || action.desc || action.text || ''
            }))
          : [];
        updatedToken.actions = processedActions;
      }

      if (newProperties.legendaryActions) {
        let processedLegendaryActions = [];
        if (Array.isArray(newProperties.legendaryActions)) {
          processedLegendaryActions = newProperties.legendaryActions.map(action => ({
            ...action,
            name: action.name || 'Unnamed Legendary Action',
            description: action.description || action.desc || action.text || '',
            desc: action.description || action.desc || action.text || '',
            text: action.description || action.desc || action.text || ''
          }));
        } else if (typeof newProperties.legendaryActions === 'object') {
          processedLegendaryActions = Object.values(newProperties.legendaryActions).map(action => ({
            ...action,
            name: action.name || 'Unnamed Legendary Action',
            description: action.description || action.desc || action.text || '',
            desc: action.description || action.desc || action.text || '',
            text: action.description || action.desc || action.text || ''
          }));
        }
        updatedToken.legendaryActions = processedLegendaryActions;
      }

      if (isMajorUpdate) {
        if (isMonster) {
          const originalProps = {
            actions: updatedToken.actions ? JSON.parse(JSON.stringify(updatedToken.actions)) : [],
            legendaryActions: updatedToken.legendaryActions ? JSON.parse(JSON.stringify(updatedToken.legendaryActions)) : [],
            hp: updatedToken.hp ? JSON.parse(JSON.stringify(updatedToken.hp)) : null,
            abilities: updatedToken.abilities ? JSON.parse(JSON.stringify(updatedToken.abilities)) : null,
            stats: updatedToken.stats ? JSON.parse(JSON.stringify(updatedToken.stats)) : null,
            strength: updatedToken.strength,
            dexterity: updatedToken.dexterity,
            constitution: updatedToken.constitution,
            intelligence: updatedToken.intelligence,
            wisdom: updatedToken.wisdom,
            charisma: updatedToken.charisma,
            initiative: updatedToken.initiative,
            speed: updatedToken.speed,
            ac: updatedToken.ac,
            skills: updatedToken.skills ? JSON.parse(JSON.stringify(updatedToken.skills)) : null
          };

          debugLog('[handleTokenUpdate] Updated monster token:', {
            name: updatedToken.name,
            hp: updatedToken.hp,
            abilities: updatedToken.abilities,
            hasMonsterData: !!updatedToken.monsterData
          });

          updatedToken = {
            ...updatedToken,
            id: updatedToken.id,
            x: updatedToken.x,
            y: updatedToken.y,
            width: updatedToken.width,
            height: updatedToken.height,
            layer: updatedToken.layer,
            type: 'monster',
            src: updatedToken.src,
            isCustom: updatedToken.isCustom,
            artwork: updatedToken.artwork || updatedToken.image
          };

          if (newProperties.actions || !updatedToken.actions || updatedToken.actions.length === 0) {
            if (originalProps.actions && originalProps.actions.length > 0) {
              updatedToken.actions = originalProps.actions;
            }
          }

          if (newProperties.legendaryActions || !updatedToken.legendaryActions || updatedToken.legendaryActions.length === 0) {
            if (originalProps.legendaryActions && originalProps.legendaryActions.length > 0) {
              updatedToken.legendaryActions = originalProps.legendaryActions;
            }
          }

          if (originalProps.abilities) {
            if (!updatedToken.abilities) {
              updatedToken.abilities = originalProps.abilities;
            }

            const normalizedMonsterAbilities = completeAbilityScores(
              resolveAbilityScores(updatedToken.abilities, updatedToken.stats, updatedToken),
              10
            );

            updatedToken.abilities = normalizedMonsterAbilities;
            updatedToken.strength = normalizedMonsterAbilities.strength;
            updatedToken.dexterity = normalizedMonsterAbilities.dexterity;
            updatedToken.constitution = normalizedMonsterAbilities.constitution;
            updatedToken.intelligence = normalizedMonsterAbilities.intelligence;
            updatedToken.wisdom = normalizedMonsterAbilities.wisdom;
            updatedToken.charisma = normalizedMonsterAbilities.charisma;
            updatedToken.stats = buildShortAbilityBlock(normalizedMonsterAbilities, 10);
          }

          if (originalProps.skills && (!updatedToken.skills || Object.keys(updatedToken.skills).length === 0)) {
            updatedToken.skills = originalProps.skills;
          }

          if (originalProps.initiative) updatedToken.initiative = originalProps.initiative;
          if (originalProps.speed) updatedToken.speed = originalProps.speed;
          if (originalProps.ac) updatedToken.ac = originalProps.ac;
        } else {
          debugLog('[handleTokenUpdate] Updated character token:', {
            name: updatedToken.name,
            hp: updatedToken.hp,
            abilities: updatedToken.abilities,
            hasCharacterData: !!updatedToken.characterData
          });

          updatedToken = {
            ...updatedToken,
            id: updatedToken.id,
            x: updatedToken.x,
            y: updatedToken.y,
            width: updatedToken.width,
            height: updatedToken.height,
            layer: updatedToken.layer,
            type: updatedToken.type,
            src: updatedToken.src,
            isCustom: updatedToken.isCustom,
            artwork: updatedToken.artwork || updatedToken.image,
            abilitiesShort: updatedToken.abilitiesShort || buildShortAbilityBlock(updatedToken.abilities, 10)
          };
        }

        debugLog(`[handleTokenUpdate] Applied full normalization for ${tokenId} major update`);
      }

      debugLog('[handleTokenUpdate] Final token state:', {
        id: updatedToken.id,
        name: updatedToken.name,
        type: updatedToken.type,
        hp: updatedToken.hp,
        abilities: updatedToken.abilities,
        stats: updatedToken.stats,
        actions: updatedToken.actions?.length || 0,
        legendaryActions: updatedToken.legendaryActions?.length || 0
      });

      if (updatedToken.abilities) {
        const normalizedAbilities = completeAbilityScores(
          resolveAbilityScores(updatedToken.abilities, updatedToken.stats, updatedToken),
          10
        );
        updatedToken.abilities = normalizedAbilities;
        updatedToken.abilitiesShort = buildShortAbilityBlock(normalizedAbilities, 10);

        updatedToken.strength = normalizedAbilities.strength;
        updatedToken.dexterity = normalizedAbilities.dexterity;
        updatedToken.constitution = normalizedAbilities.constitution;
        updatedToken.intelligence = normalizedAbilities.intelligence;
        updatedToken.wisdom = normalizedAbilities.wisdom;
        updatedToken.charisma = normalizedAbilities.charisma;
        updatedToken.stats = { ...updatedToken.abilitiesShort };
      }

      updatedToken.statusEffects = nextStatusEffects;
      updatedToken.statusEffectsMeta = nextStatusEffectsMeta;

      debugLog('[handleTokenUpdate] Final data consistency validation:', {
        hp: updatedToken.hp,
        abilities: updatedToken.abilities ? 'Present' : 'Missing',
        abilitiesShort: updatedToken.abilitiesShort ? 'Present' : 'Missing',
        stats: updatedToken.stats ? 'Present' : 'Missing',
        artwork: updatedToken.artwork ? 'Present' : 'Missing'
      });

      const isEqual = (a, b) => {
        if (a === b) return true;
        if (typeof a !== typeof b) return false;
        if (a && typeof a === 'object') {
          try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
        }
        return false;
      };
      const defaultCompareKeys = ['x','y','gridX','gridY','width','height','size','hp','hpObj','maxHp','abilities','abilitiesShort','stats','actions','legendaryActions','ac','initiative','layer','type','src','artwork','visible','opacity','rotation','statusEffects','statusEffectsMeta'];
      const keysToCompare = new Set([...(processedProperties ? Object.keys(processedProperties) : []), ...defaultCompareKeys]);
      let noChange = true;
      for (const k of keysToCompare) {
        if (!isEqual(currentToken[k], updatedToken[k])) { noChange = false; break; }
      }
      if (noChange) {
        debugLog(`[handleTokenUpdate] No effective changes for token ${tokenId}, skipping state update.`);
        return prevTokens;
      }

      const updatedTokens = [...prevTokens];
      updatedTokens[tokenIndex] = updatedToken;

      // React state updaters may run after handleTokenUpdate returns. Queue the
      // multiplayer write here and flush it from an effect after `tokens` commits;
      // reading a locally mutated outcome synchronously skipped persistence in
      // concurrent roots.
      // date_of_change: 2026-07-15
      pendingTokenStateSyncRef.current = {
        tokenId,
        updatedProperties: newProperties,
        updatedToken
      };

      debugLog(`[handleTokenUpdate] Updated token ${tokenId} with normalized properties:`,
        isMajorUpdate ? 'Major update with full normalization' : 'Minor update with targeted normalization');

      return updatedTokens;
    });

    return true;
  }, [tokens, isMultiplayerMode, canControlToken, isGameMaster, currentUser]);

  useEffect(() => {
    const pendingSync = pendingTokenStateSyncRef.current;
    if (!pendingSync) return;
    pendingTokenStateSyncRef.current = null;
    if (!isMultiplayerMode) return;

    // `tokens` is now the committed collection containing the engine update, so
    // the bridge persists the same canonical state the targeting engine reads.
    syncStateToMultiplayer('token_update', pendingSync, tokens);
  }, [tokens, isMultiplayerMode, syncStateToMultiplayer]);

  /**
   * Apply multiple token updates atomically.
   * @date_of_change: 2025-10-08
   *
   * Why: Sequential handleTokenUpdate() calls during group moves can race with state closures,
   * causing earlier tokens to revert. This batches updates into a single setTokens() and
   * a single multiplayer sync using fresh updatedTokens.
   *
   * Limitations: Designed for lightweight property merges (e.g., x/y/gridX/gridY, layer).
   * Does not run the full normalization pipeline from handleTokenUpdate(). For complex
   * updates (abilities, hp structures, actions), call handleTokenUpdate individually.
   */
  const handleBatchTokenUpdates = useCallback((updatesArray = [], options = {}) => {
    try {
      if (!Array.isArray(updatesArray) || updatesArray.length === 0) return false;

      // Build updatedTokens using a working copy to avoid stale-closure overwrites
      let working = [...tokens];
      const applied = [];
      const actionEconomySpends = [];

      for (const entry of updatesArray) {
        if (!entry) continue;
        const tokenId = entry.id || entry.tokenId;
        const props = entry.props || entry.newProperties || {};
        if (!tokenId || typeof props !== 'object') continue;

        // Multiplayer permission checks. Engine updates are canonical rules
        // outcomes and use the same explicit bypass as handleTokenUpdate.
        // date_of_change: 2026-07-18
        if (isMultiplayerMode) {
          const hasPermission = canControlToken(tokenId)
            || (isGameMaster && entry._gmOverride === true)
            || entry._engineUpdate === true;
          if (!hasPermission) continue;
        }

        const idx = working.findIndex(t => t.id === tokenId);
        if (idx === -1) continue;
        const current = working[idx];

        if (props.combatFlags && typeof props.combatFlags === 'object') {
          const localEconomyState = ActionEconomyManager.getCombatantState(tokenId);
          actionEconomySpends.push(...buildActionEconomySpendRequests({
            tokenId,
            currentCombatFlags: current.combatFlags || {},
            nextCombatFlags: props.combatFlags,
            actionsThisTurn: localEconomyState?.actionsThisTurn || [],
          }));
        }

        // date_of_change: 2025-10-11 - Normalize nested grid into gridX/gridY for compatibility
        // rule_reference: Grid coordinates are the source of truth for adjacency/range. Some callers
        // now provide `grid: { x, y }` (preferred by Token.js). Ensure legacy `gridX/gridY` are kept
        // in sync so downstream systems (e.g., RangeCalculator) operate correctly.
        let propsNormalized = props;
        if (props && typeof props.grid === 'object' && props.grid !== null) {
          const gx = Number(props.grid.x);
          const gy = Number(props.grid.y);
          propsNormalized = {
            ...props,
            ...(Number.isFinite(gx) ? { gridX: gx } : {}),
            ...(Number.isFinite(gy) ? { gridY: gy } : {})
          };
        }

        // Shallow merge for positional/lightweight properties; preserve other fields
        const updateOrigin = entry._engineUpdate === true
          ? 'engine'
          : (entry._gmOverride === true ? 'gm' : currentUser?.userId);
        const merged = {
          ...current,
          ...propsNormalized,
          ...(isMultiplayerMode ? { _lastModifiedBy: updateOrigin, _lastModified: Date.now() } : {})
        };

        working[idx] = merged;
        applied.push({ tokenId, props });
      }

      if (applied.length === 0) return false;

      // Collapse repeated updates for one token before crossing the service
      // boundary. A single rules action may stage economy, HP, and conditions
      // separately, but the authoritative mutation must contain one coherent
      // patch per token. date_of_change: 2026-07-18
      const appliedByToken = new Map();
      applied.forEach(({ tokenId, props }) => {
        const previous = appliedByToken.get(tokenId) || {};
        appliedByToken.set(tokenId, {
          ...previous,
          ...props,
          ...(previous.combatFlags || props.combatFlags ? {
            combatFlags: { ...(previous.combatFlags || {}), ...(props.combatFlags || {}) }
          } : {}),
          ...(previous.statusEffects || props.statusEffects ? {
            statusEffects: { ...(previous.statusEffects || {}), ...(props.statusEffects || {}) }
          } : {}),
          ...(previous.statusEffectsMeta || props.statusEffectsMeta ? {
            statusEffectsMeta: { ...(previous.statusEffectsMeta || {}), ...(props.statusEffectsMeta || {}) }
          } : {})
        });
      });
      const consolidatedApplied = [...appliedByToken.entries()].map(([tokenId, props]) => ({ tokenId, props }));

      // Rules actions can request an authoritative commit. In that mode the
      // GMA outbox must confirm the snapshot patch before local React state is
      // advanced and before the caller emits its durable combat log.
      // rule_reference: AGENTS.md - session snapshot is the only mutable source of truth.
      // date_of_change: 2026-07-18
      if (isMultiplayerMode && options.awaitAuthoritative === true) {
        const syncData = { batch: true, updates: consolidatedApplied };
        const commitEconomy = actionEconomySpends.reduce((sequence, spend) => (
          sequence.then(async () => {
            const acknowledgement = await sendBattleRoomMutation('action:economy:spend', spend);
            await waitForAuthoritativeRevision(acknowledgement?.revision);
          })
        ), Promise.resolve());
        return commitEconomy.then(() => syncStateToMultiplayer('token_update', syncData, working)).then((confirmed) => {
          if (confirmed === false) return false;
          return true;
        }).catch((error) => {
          console.error('[useBattleRoom] authoritative batch token update failed:', error);
          return false;
        });
      }

      setTokens(working);

      // Multiplayer: send single consolidated map update with fresh tokens
      if (isMultiplayerMode) {
        const syncData = { batch: true, updates: consolidatedApplied };
        actionEconomySpends.reduce((sequence, spend) => (
          sequence.then(() => sendBattleRoomMutation('action:economy:spend', spend))
        ), Promise.resolve())
          .then(() => syncStateToMultiplayer('token_update', syncData, working))
          .catch((error) => console.error('[useBattleRoom] action-economy synchronization failed:', error));
      }

      return true;
    } catch (err) {
      console.error('[useBattleRoom] handleBatchTokenUpdates error:', err);
      return false;
    }
  }, [tokens, isMultiplayerMode, syncStateToMultiplayer, sendBattleRoomMutation, waitForAuthoritativeRevision, canControlToken, isGameMaster, currentUser]);

  /**
   * Execute an attack from one token to another
   * @param {string} attackerId - Attacker token ID
   * @param {string} targetId - Target token ID
   * @param {object} action - Attack action object (expects name, bonus/attackBonus, damage/damageFormula)
   * @param {object} options - Additional options for validation/rolls
   * @returns {object} Attack result from CombatManager.executeAttack
   */
  const handleExecuteAttack = useCallback((attackerId, targetId, action, options = {}) => {
    try {
      if (!attackerId || !targetId || !action) {
        return { error: true, message: '[handleExecuteAttack] Missing attackerId, targetId, or action' };
      }

      // Resolve tokens from current state
      const attackerToken = tokens.find(t => t.id === attackerId);
      const targetToken = tokens.find(t => t.id === targetId);
      if (!attackerToken || !targetToken) {
        return { error: true, message: `[handleExecuteAttack] Token(s) not found: attacker=${!!attackerToken}, target=${!!targetToken}` };
      }

      // Build lightweight battle state for validation (grid + tokens)
      const currentBattleState = {
        grid: { size: gridSize, width: gridWidth, height: gridHeight, showGrid },
        tokens,
        layers,
        mapState
      };

      // Execute the attack using CombatManager
      const attackResult = CombatManager.executeAttack(
        attackerToken,
        targetToken,
        action,
        currentBattleState,
        options
      );

      // If we have an updated target entity with HP changes, reflect it on the target token immutably
      if (attackResult && attackResult.updatedTarget) {
        const updated = attackResult.updatedTarget;
        const newCurrent = updated.hpObj?.current ?? updated.hp ?? (targetToken.hpObj?.current ?? targetToken.hp ?? 0);
        const newMax = updated.hpObj?.max ?? updated.maxHp ?? (targetToken.hpObj?.max ?? targetToken.maxHp ?? undefined);

        const newProps = {
          hpObj: {
            ...(targetToken.hpObj || {}),
            current: newCurrent,
            ...(newMax !== undefined ? { max: newMax } : {})
          },
          hp: newCurrent,
          ...(newMax !== undefined ? { maxHp: newMax } : {})
        };

        handleTokenUpdate(targetId, newProps);
      }

      if (attackResult?.unseenAttack) {
        const normalizeCondition = (value) => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
        const revealConditions = new Set(['hidden', 'unseen']);
        const currentConditions = Array.isArray(attackerToken.conditions) ? attackerToken.conditions : [];
        const nextConditions = currentConditions.filter((condition) => !revealConditions.has(normalizeCondition(condition)));
        const currentHiddenFrom = Array.isArray(attackerToken.combatFlags?.hiddenFrom)
          ? attackerToken.combatFlags.hiddenFrom
          : [];
        const nextHiddenFrom = currentHiddenFrom.filter((observerId) => (
          !['*', 'all', 'everyone'].includes(String(observerId || '').toLowerCase())
          && String(observerId) !== String(targetToken.id)
          && String(observerId) !== String(targetToken.combatantId)
          && String(observerId) !== String(targetToken.name)
        ));
        const conditionsChanged = nextConditions.length !== currentConditions.length;
        const hiddenFromChanged = nextHiddenFrom.length !== currentHiddenFrom.length;

        if (conditionsChanged || hiddenFromChanged) {
          handleTokenUpdate(attackerId, {
            ...(conditionsChanged ? { conditions: nextConditions } : {}),
            ...(hiddenFromChanged ? { combatFlags: { ...(attackerToken.combatFlags || {}), hiddenFrom: nextHiddenFrom } } : {}),
            _engineUpdate: true
          });
        }
      }

      // Structured log for the action with detailed roll results
      try {
        const hit = !!attackResult?.attackRoll?.isHit;
        const damageTotal = attackResult?.damage?.total ?? (hit ? 0 : undefined);
        
        // Build detailed result object with roll information
        const detailedResult = {
          hit,
          damage: damageTotal,
          // Attack roll details
          attackRoll: attackResult?.attackRoll ? {
            total: attackResult.attackRoll.total,
            d20Roll: attackResult.attackRoll.roll, // map from CombatManager.calculateAttackRoll
            modifier: attackResult.attackRoll.attackBonus,
            advantage: attackResult.attackRoll.advantage,
            disadvantage: attackResult.attackRoll.disadvantage,
            isCritical: attackResult.attackRoll.isCritical,
            isCriticalFailure: attackResult.attackRoll.isCriticalFail
          } : null,
          // Damage roll details
          damageRoll: attackResult?.damage ? {
            formula: attackResult.damage.details?.[0]?.formula,
            rolls: attackResult.damage.details?.[0]?.rolls,
            total: attackResult.damage.total,
            isCritical: attackResult.damage.isCritical
          } : null,
          // Target AC for comparison
          targetAC: attackResult?.attackRoll?.targetAC ?? (targetToken?.ac || targetToken?.entity?.ac)
        };
        
        // Prefer specific action name if provided, and attach originalAction for UI rendering fallbacks
        const actionForLog = {
          name: action?.originalAction?.name || action?.name,
          type: action?.type || 'attack',
          bonus: action?.bonus || action?.attackBonus,
          damage: action?.damage || action?.damageFormula,
          originalAction: action?.originalAction || action
        };
        CombatLogManager.logAction(attackerId, actionForLog, [targetToken], detailedResult);
      } catch (logErr) {
        debugLog('[handleExecuteAttack] Failed structured logAction:', logErr);
      }

      // Enhanced D&D 5e combat message log with detailed mechanics
      try {
        if (attackResult && attackResult.attackRoll) {
          const msg = CombatManager.formatEnhancedAttackMessage(attackResult);
          if (msg) CombatLogManager.logMessage(msg, { combatantId: attackerId });
        }
      } catch (msgErr) {
        debugLog('[handleExecuteAttack] Failed enhanced combat logMessage:', msgErr);
      }

      // Spend action economy for attacker (use mapping helper on manager by action name)
      try {
        // @date_of_change 2025-09-09 - CRITICAL DEBUG: Log action structure to find [object Object] issue
        debugLog('🔍 [ActionEconomy DEBUG] Action structure investigation:', {
          action,
          actionName: action.name,
          actionNameType: typeof action.name,
          actionNameValue: action.name,
          isObjectName: typeof action.name === 'object',
          actionKeys: typeof action === 'object' ? Object.keys(action) : 'not-object'
        });
        
        // Fix: Ensure action name is always a string
        const actionName = typeof action.name === 'string' ? action.name : 
                          (typeof action.name === 'object' && action.name?.name) ? action.name.name :
                          'Attack';
        
        const actionType = ActionEconomyManager.getActionType(actionName);
        debugLog('🔍 [ActionEconomy DEBUG] Using corrected action name:', { actionName, actionType });
        
        // Bracket notation avoids react-hooks false positive on method named 'useAction'
        ActionEconomyManager['useAction'](attackerId, actionType, actionName, options);
      } catch (aeErr) {
        debugLog('[handleExecuteAttack] Failed to record action economy usage:', aeErr);
      }

      return attackResult;
    } catch (error) {
      console.error('[handleExecuteAttack] Unexpected error:', error);
      return { error: true, message: error.message };
    }
  }, [tokens, gridSize, gridWidth, gridHeight, showGrid, layers, mapState, handleTokenUpdate]);

  const handleMapDragEnd = useCallback((e) => {
    if (isLayerInteractive('map')) {
      const newPos = { x: e.target.x(), y: e.target.y() };
      setMapState(prev => ({ ...prev, position: newPos }));
      
      // Also update the position in session data for proper save/load
      debugLog('[handleMapDragEnd] Map position updated:', newPos);
    }
  }, [isLayerInteractive]);

  // Deselection handler for clicking on empty areas
  const handleDeselectAll = useCallback(() => {
    setSelectedTokenId(null);
    setMapState(prev => ({ ...prev, isSelected: false }));
  }, []);

  /**
   * Handle token selection - used by Initiative Tracker auto-select and general token selection
   */
  const handleTokenSelect = useCallback((tokenId, layer = 'token') => {
    const normalizedLayerId = normalizeBattleLayerId(layer);
    setSelectedTokenId(tokenId);
    // Deselect map when selecting a token
    setMapState(prev => ({ ...prev, isSelected: false }));
    
    // Switch to the token's layer if provided
    if (normalizedLayerId && normalizedLayerId !== normalizeBattleLayerId(activeLayer, 'map')) {
      setActiveLayer(normalizedLayerId);
    }
  }, [activeLayer, setActiveLayer]);

  /**
   * Unified token update handler for keeping token properties in sync
   * 
   * This handler ensures that token property changes made anywhere in the app
   * (token properties popup, character sheet, etc.) are properly updated in the
   * central token state and reflected across all components.
   * 
   * @param {string} tokenId - ID of the token to update
   * @param {object} newProperties - New properties to apply to the token
   * @returns {boolean} - Success status of the update
   */
  /**
   * Deletes a token from the battle map
   * 
   * @param {string} tokenId - ID of the token to delete
   * @returns {boolean} - Success status of the deletion
   */
  const handleTokenDelete = useCallback((tokenId) => {
    if (!tokenId) {
      debugLog('[handleTokenDelete] No token ID provided');
      return false;
    }
    
    // Filter out the token with the specified ID
    const updatedTokens = tokens.filter(token => token.id !== tokenId);
    
    // Check if the token was found and removed
    if (updatedTokens.length === tokens.length) {
      debugLog(`[handleTokenDelete] Token with id ${tokenId} not found`);
      return false;
    }
    
    // Update the tokens array
    setTokens(updatedTokens);

    // Sync to multiplayer session if active
    // date_of_change: 2025-09-24 - Added multiplayer state sync
    if (isMultiplayerMode) {
      const syncData = {
        tokenId: tokenId,
        deletedToken: tokens.find(t => t.id === tokenId)
      };
      syncStateToMultiplayer('token_delete', syncData);
    }

    // If the deleted token was selected, deselect it
    if (selectedTokenId === tokenId) {
      setSelectedTokenId(null);
    }

    debugLog(`[handleTokenDelete] Deleted token ${tokenId}`);
    return true;
  }, [tokens, selectedTokenId, isMultiplayerMode, syncStateToMultiplayer]);
  
  /**
   * Deletes a map from the battle map
   * 
   * @param {string} mapId - ID of the map to delete (optional, defaults to current map)
   * @returns {boolean} - Success status of the deletion
   */
  const handleMapDelete = useCallback((mapId = null) => {
    // Clear all map state across all storage mechanisms
    // 1. Legacy map image
    setMapImage(null);
    
    // 2. Map state
    setMapState({
      position: { x: 0, y: 0 },
      size: { width: 800, height: 600 },
      isSelected: false
    });
    
    // 3. Layer-aware map images
    setMapImages(prev => ({
      ...prev,
      map: null
    }));
    
    // 4. Layer state
    setLayers(prev => ({
      ...prev,
      map: {
        ...prev.map,
        mapImage: null
      }
    }));
    
    debugLog('[handleMapDelete] Map deleted from all state locations');
    return true;
  }, []);

  const handleMapUpload = useCallback((fileOrEvent) => {
    debugLog('[useBattleRoom] handleMapUpload called with:', fileOrEvent);
    return new Promise((resolve, reject) => {
      try {
        // Handle both File objects and event objects
        const file = fileOrEvent && fileOrEvent.target ? fileOrEvent.target.files[0] : fileOrEvent;

        // Determine target layer early, default to 'map'
        const inferredTargetLayer = (file && file.targetLayer) ? file.targetLayer : 'map';

        // Support direct URL strings or asset objects with a url property
        if (typeof file === 'string' || (file && typeof file.url === 'string')) {
          const dataUrl = resolveUploadAssetUrl(typeof file === 'string' ? file : file.url);
          const targetLayer = inferredTargetLayer;

          // Update ALL map state variables for complete synchronization
          setMapImage(dataUrl);
          setMapImages(prev => ({
            ...prev,
            [targetLayer]: dataUrl
          }));
          setLayers(prev => ({
            ...prev,
            [targetLayer]: {
              ...prev[targetLayer],
              mapImage: dataUrl
            }
          }));
          setMapState(prev => ({
            ...prev,
            isSelected: true
          }));

          // Sync to multiplayer session if active
          // date_of_change: 2025-09-25 - Added multiplayer sync for map uploads
          if (isMultiplayerMode) {
            const syncData = {
              mapImage: dataUrl,
              targetLayer: targetLayer,
              mapImages: { [targetLayer]: dataUrl },
              layers: { [targetLayer]: { mapImage: dataUrl } }
            };
            syncStateToMultiplayer('map_update', syncData);
          }

          debugLog(`[useBattleRoom] Map image (URL) updated across all state variables for ${targetLayer}`);
          resolve(dataUrl);
          return;
        }

        if (!file) {
          const error = 'No file provided for map upload';
          console.error('[useBattleRoom]', error);
          reject(new Error(error));
          return;
        }

        if (!file.type || !file.type.startsWith('image/')) {
          const error = `Invalid file type: ${file.type || 'unknown'}. Must be an image file.`;
          console.error('[useBattleRoom]', error);
          reject(new Error(error));
          return;
        }

        const targetLayer = inferredTargetLayer;
        console.log(`[useBattleRoom] Processing map upload (${file.name}, ${(file.size / 1024).toFixed(2)}KB) to layer: ${targetLayer}`);

        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const dataUrl = e.target.result;
            debugLog(`[useBattleRoom] File read successfully (${dataUrl.length} chars)`);

            // Update ALL map state variables for complete synchronization
            // 1. Update the legacy mapImage for backward compatibility
            setMapImage(dataUrl);

            // 2. Update the layer-aware map images state
            setMapImages(prev => ({
              ...prev,
              [targetLayer]: dataUrl
            }));

            // 3. Update the layer state directly
            setLayers(prev => ({
              ...prev,
              [targetLayer]: {
                ...prev[targetLayer],
                mapImage: dataUrl
              }
            }));

            // 4. Set map as selected
            setMapState(prev => ({
              ...prev,
              isSelected: true
            }));

            // 5. Sync to multiplayer session if active
            // date_of_change: 2025-09-25 - Added multiplayer sync for map file uploads
            if (isMultiplayerMode) {
              const syncData = {
                mapImage: dataUrl,
                targetLayer: targetLayer,
                mapImages: { [targetLayer]: dataUrl },
                layers: { [targetLayer]: { mapImage: dataUrl } }
              };
              syncStateToMultiplayer('map_update', syncData);
            }

            debugLog(`[useBattleRoom] Map image updated across all state variables for ${targetLayer}`);

            resolve(dataUrl);
          } catch (innerError) {
            console.error('[useBattleRoom] Error in FileReader onload handler:', innerError);
            reject(innerError);
          }
        };

        reader.onerror = (error) => {
          console.error('[useBattleRoom] FileReader error:', error);
          reject(new Error(`Failed to read file: ${error.message || 'Unknown error'}`));
        };

        reader.onloadend = () => {
          debugLog('[useBattleRoom] FileReader loadend event fired');
        };

        debugLog('[useBattleRoom] Starting file read...');
        reader.readAsDataURL(file);
        
      } catch (error) {
        console.error('[useBattleRoom] Error in handleMapUpload:', error);
        reject(error);
      }
    });
  }, [isMultiplayerMode, syncStateToMultiplayer]);

  // @date_of_change: 2025-10-15 - Hoisted above consumers to prevent TDZ ReferenceError when player-only build loads BattleRoom
  const pixelToGrid = useCallback((pixelX, pixelY, size) => {
    return {
      x: Math.floor(pixelX / size),
      y: Math.floor(pixelY / size)
    };
  }, []);
  
  // Raw images are scene media, never actors. Canonical characters and monsters
  // continue through createTokenFromDashboardData; callers that still reach this
  // retired path fail explicitly. date_of_change: 2026-09-11
  const handleTokenUpload = useCallback(() => (
    Promise.reject(new Error(
      'Raw images cannot create combat tokens. Place the image as a scene asset, or add a canonical character or monster from the roster.'
    ))
  ), []);
  /**
   * Grid snapping utility function - CORE WORKING IMPLEMENTATION
   * 
   * This function provides precise token snapping to grid intersections.
   * Algorithm: Math.round(position / gridSize) * gridSize
   * 
   * @param {number} x - Raw X position from drag event
   * @param {number} y - Raw Y position from drag event (optional, if not provided, x is treated as single position)
   * @returns {object|number} - Snapped coordinates {x, y} or single snapped position
   */
  const snapToGridPosition = useCallback((x, y) => {
    if (!snapToGrid) {
      // If y is not provided, return single position
      return y !== undefined ? { x, y } : x;
    }
    
    if (y !== undefined) {
      // Two parameters: return snapped coordinates object
      const snappedX = Math.round(x / gridSize) * gridSize;
      const snappedY = Math.round(y / gridSize) * gridSize;
      return { x: snappedX, y: snappedY };
    } else {
      // Single parameter: return snapped position
      return Math.round(x / gridSize) * gridSize;
    }
  }, [snapToGrid, gridSize]);

  /**
   * D&D 5e Size Categories and their grid square dimensions
   * Using useMemo to prevent dependency issues
   */
  const sizeCategories = useMemo(() => ({
    'Tiny': 0.5,      // Half a grid square
    'Small': 1,       // 1x1 grid square
    'Medium': 1,      // 1x1 grid square
    'Large': 2,       // 2x2 grid squares
    'Huge': 3,        // 3x3 grid squares
    'Gargantuan': 4   // 4x4 grid squares
  }), []);

  /**
   * Calculate token pixel size based on D&D size category and current grid size
   */
  const getTokenSizeFromCategory = useCallback((sizeCategory) => {
    const gridSquares = sizeCategories[sizeCategory] || 1; // Default to Medium (1x1)
    return gridSquares * gridSize;
  }, [gridSize, sizeCategories]);

  /**
   * Create token from dashboard data (monsters or characters)
   * 
   * This function creates a new token from dashboard data, normalizing data structure
   * and preserving HP values consistently across all paths.
   * 
   * @date_of_change 2025-08-22 Fixed HP preservation issue for loaded monster tokens
   * @param {Object} entityData - Monster or character data from dashboard
   * @param {string} targetLayer - Target layer for the new token ('token' by default)
   * @returns {Promise<Object>} - Promise resolving to the created token
   */
  /**
   * Derive combat actions from character data (spells, equipment, features)
   * @date_of_change 2025-09-06 - ASSET PLACEMENT DATA LOSS FIX
   * Characters don't have direct "actions" like monsters - need to derive from structured data
   */
  const deriveActionsFromCharacterData = useCallback((entityData, displayData) => {
    if (typeof buildCombatActionCatalog === 'function') {
      const catalog = buildCombatActionCatalog(entityData);
      debugLog('[deriveActionsFromCharacterData] Shared combat action catalog resolved:', {
        name: entityData?.name,
        totalActions: catalog.length,
        actionNames: catalog.map((entry) => `${entry.name} (${entry.actionType || entry.activationType || entry.type})`)
      });
      return catalog;
    }

    const actions = [];
    
    // @date_of_change 2025-09-06 - CRITICAL FIX: Check EntityStorage AND CustomAsset canonicalData structures
    // Following AGENTS.md zero transformation - access all possible data paths
    // EntityStorage: entityData.data, CustomAsset: entityData.canonicalData, Root: entityData
    const characterData = entityData.canonicalData || entityData.data || entityData;
    // @date_of_change 2025-09-06 - CRITICAL: Custom characters use 'spells' not 'spellcasting' 
    const spellcastingData = characterData.spellcasting || characterData.data?.spellcasting || entityData.canonicalData?.spellcasting || characterData.spells || entityData.canonicalData?.spells || entityData.spellcasting;
    const equipmentData = characterData.equipment || characterData.data?.equipment || entityData.canonicalData?.equipment || entityData.equipment;
    const featuresData = characterData.features || characterData.data?.features || entityData.canonicalData?.features || entityData.features;
    const spellsData = characterData.spells || characterData.data?.spells || entityData.canonicalData?.spells || entityData.spells;
    
    debugLog('[deriveActionsFromCharacterData] Deriving actions from:', {
      name: characterData.name || entityData.name,
      hasSpellcasting: !!spellcastingData,
      hasEquipment: !!equipmentData,
      hasFeatures: !!featuresData,
      hasSpells: !!spellsData,
      preparedSpellCount: spellcastingData?.prepared?.length || 0,
      weaponCount: equipmentData?.weapons?.length || 0,
      featureCount: Array.isArray(featuresData) ? featuresData.length : 0
    });

    // 1. WEAPON ATTACKS - Convert equipment weapons to attack actions
    if (equipmentData?.weapons && Array.isArray(equipmentData.weapons)) {
      equipmentData.weapons.forEach(weapon => {
        // @date_of_change 2025-09-06 - WEAPON TYPE CLASSIFICATION FIX
        // Properly classify weapons by type and targeting
        const weaponName = weapon.name || 'Unknown Weapon';
        const isRanged = weapon.properties?.includes('Ranged') || 
                        weaponName.toLowerCase().includes('crossbow') || 
                        weaponName.toLowerCase().includes('bow') ||
                        weaponName.toLowerCase().includes('dart');
        
        // Determine if weapon is defensive (shields) or offensive
        const isDefensive = weaponName.toLowerCase().includes('shield');
        
        const attackAction = {
          name: weaponName,
          type: 'weapon',
          category: 'action',
          description: isDefensive ? `Use ${weaponName} for defense` : `Attack with ${weaponName}`,
          // CRITICAL: Add proper targeting - weapons target enemies (except shields)
          targetingType: isDefensive ? 'self' : 'enemy',
          actionType: 'action',
          attack: isDefensive ? undefined : {
            type: isRanged ? 'rangedWeapon' : 'meleeWeapon',
            toHit: weapon.attackBonus || 0,
            reach: weapon.properties?.includes('Reach') ? 10 : (isRanged ? 120 : 5),
            damage: [{
              dice: weapon.damage || '1d4',
              type: weapon.damageType || 'bludgeoning'
            }],
            notes: `${isRanged ? 'Ranged' : 'Melee'} Weapon Attack${weapon.properties ? ` (${weapon.properties.join(', ')})` : ''}`
          },
          // Store original weapon data for detailed display
          weaponData: weapon
        };
        
        debugLog(`[deriveActionsFromCharacterData] Classified weapon: ${weaponName} - targeting: ${attackAction.targetingType}, type: ${isRanged ? 'ranged' : 'melee'}, defensive: ${isDefensive}`);
        actions.push(attackAction);
      });
    }

    // 2. SPELLS - Convert all available spells to spell actions
    // @date_of_change 2025-09-12 - CRITICAL FIX: Handle ALL spell formats, not just prepared
    // Process spells from multiple storage formats: prepared, known, level arrays, etc.
    const allSpells = [];

    // Collect spells from prepared list
    // @date_of_change 2025-09-14 - CRITICAL FIX: Handle both string IDs and full spell objects in prepared list
    if (spellcastingData?.prepared && Array.isArray(spellcastingData.prepared)) {
      spellcastingData.prepared.forEach(spell => {
        if (typeof spell === 'string') {
          allSpells.push({ id: spell, source: 'prepared' });
        } else if (spell && typeof spell === 'object' && spell.name) {
          // Full spell object - store both id and full data
          allSpells.push({ id: spell.name, source: 'prepared', spellData: spell });
        }
      });
    }

    // Collect spells from known list
    if (spellsData?.known && Array.isArray(spellsData.known)) {
      spellsData.known.forEach(spellName => allSpells.push({ id: spellName, source: 'known' }));
    }

    // Collect spells from level arrays (level0/cantrips through level9)
    if (spellsData) {
      // Check cantrips (level 0)
      if (spellsData.cantrips && Array.isArray(spellsData.cantrips)) {
        spellsData.cantrips.forEach(spell => {
          const spellId = typeof spell === 'string' ? spell : (spell.name || spell.id);
          if (spellId) allSpells.push({ id: spellId, source: 'cantrips', spellData: spell });
        });
      }

      // Check level-based spell arrays
      for (let level = 1; level <= 9; level++) {
        const levelKey = `level${level}`;
        if (spellsData[levelKey] && Array.isArray(spellsData[levelKey])) {
          spellsData[levelKey].forEach(spell => {
            const spellId = typeof spell === 'string' ? spell : (spell.name || spell.id);
            if (spellId) allSpells.push({ id: spellId, source: levelKey, level, spellData: spell });
          });
        }
      }
    }

    // Collect spells from atWill
    // @date_of_change 2025-09-13 - FULL EMBED SUPPORT: Handle string and object entries
    if (spellsData?.atWill && Array.isArray(spellsData.atWill)) {
      spellsData.atWill.forEach(spell => {
        if (typeof spell === 'string') {
          allSpells.push({ id: spell, source: 'atWill' });
        } else if (spell && typeof spell === 'object') {
          const id = spell.name || spell.id || '';
          allSpells.push({ id, source: 'atWill', spellData: spell });
        }
      });
    }

    debugLog('[deriveActionsFromCharacterData] Found spells:', {
      totalSpells: allSpells.length,
      sources: [...new Set(allSpells.map(s => s.source))],
      spellNames: allSpells.map(s => s.id).slice(0, 10) // First 10 for debugging
    });

    // Convert all found spells to actions
    allSpells.forEach(({ id: spellId, source, level, spellData: preloadedSpellData }) => {
      // Try to find full spell data
      let spellData = preloadedSpellData || null;

      // If we don't have preloaded data, search for it
      if (!spellData && spellsData) {
        // Look in cantrips
        if (spellsData.cantrips) {
          // @date_of_change 2025-09-13 - Normalize target id for comparison even when spellId is an object
          const targetSlug = (typeof spellId === 'string' ? spellId : (spellId?.name || spellId?.id || ''))
            .toLowerCase()
            .replace(/[^a-z]/g, '');
          spellData = spellsData.cantrips.find(s => {
            if (typeof s === 'object') {
              const sSlug = (s.name || s.id || '').toLowerCase().replace(/[^a-z]/g, '');
              return s.id === spellId || sSlug === targetSlug;
            }
            if (typeof s === 'string') {
              return s.toLowerCase().replace(/[^a-z]/g, '') === targetSlug;
            }
            return false;
          });
        }
        // Look in leveled spells
        for (let searchLevel = 1; searchLevel <= 9; searchLevel++) {
          const levelKey = `level${searchLevel}`;
          if (spellsData[levelKey] && Array.isArray(spellsData[levelKey])) {
            // @date_of_change 2025-09-13 - Normalize target id for comparison
            const targetSlug = (typeof spellId === 'string' ? spellId : (spellId?.name || spellId?.id || ''))
              .toLowerCase()
              .replace(/[^a-z]/g, '');
            const found = spellsData[levelKey].find(s => {
              if (typeof s === 'object') {
                const sSlug = (s.name || s.id || '').toLowerCase().replace(/[^a-z]/g, '');
                return s.id === spellId || sSlug === targetSlug;
              }
              if (typeof s === 'string') {
                return s.toLowerCase().replace(/[^a-z]/g, '') === targetSlug;
              }
              return false;
            });
            if (found) {
              spellData = found;
              break;
            }
          }
        }
      }

      // @date_of_change 2025-09-16 - Enhanced debugging for spell data resolution
      // Handle both string references and full spell objects
      debugLog(`[deriveActionsFromCharacterData] SPELL_DATA_DEBUG for ${spellId}:`, {
        spellId,
        spellDataType: typeof spellData,
        hasSpellData: !!spellData,
        spellDataKeys: spellData ? Object.keys(spellData) : [],
        spellDataSample: spellData
      });

      const isFullSpellObject = typeof spellData === 'object' && spellData !== null && spellData.name;
      debugLog(`[deriveActionsFromCharacterData] SPELL_OBJECT_DEBUG:`, {
        isFullSpellObject,
        hasName: !!(spellData && spellData.name),
        hasDescription: !!(spellData && spellData.description),
        hasLevel: !!(spellData && spellData.level)
      });

      const spellName = isFullSpellObject ? spellData.name :
                       (typeof spellId === 'string' ? spellId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) :
                        (spellId?.name || spellId?.id || String(spellId || 'Unknown Spell')));
      const spellRange = isFullSpellObject ? spellData.range : 'Self';
      const spellSchool = isFullSpellObject ? spellData.school : 'Unknown';

      // Determine spell targeting type based on name, school, and description
      let targetingType = 'enemy'; // default
      let actionType = 'action'; // default

      // @date_of_change 2025-09-13 - Derive a safe lowercase name for classification
      const nameLower = (typeof spellName === 'string' ? spellName : String(spellName || ''))
        .toLowerCase();

      // Healing spells - target self or allies
      if (nameLower.includes('heal') ||
          nameLower.includes('cure') ||
          (typeof spellId === 'string' && (spellId.toLowerCase().includes('cure') || spellId.toLowerCase().includes('heal')))) {
        targetingType = 'ally';
      }
      // Buff/utility spells - target self or allies
      else if (nameLower.includes('bless') ||
               nameLower.includes('shield') ||
               nameLower.includes('sanctuary') ||
               spellSchool === 'Abjuration' ||
               spellSchool === 'Enchantment') {
        targetingType = 'ally';
      }
      // Self-only spells
      else if (spellRange === 'Self' || spellRange === 'Personal') {
        targetingType = 'self';
      }
      // Attack spells - target enemies
      else if (nameLower.includes('bolt') ||
               nameLower.includes('missile') ||
               nameLower.includes('fire') ||
               spellSchool === 'Evocation' ||
               spellSchool === 'Necromancy') {
        targetingType = 'enemy';
      }

      // Determine if it's bonus action spell
      if (spellData?.castingTime?.includes('bonus') || nameLower.includes('healing word')) {
        actionType = 'bonus_action';
      }

      const spellAction = {
        name: spellName,
        type: 'spell',
        category: isFullSpellObject && spellData.level === 0 ? 'cantrip' : `level-${isFullSpellObject ? spellData.level : 1}`,
        description: isFullSpellObject ? spellData.description : `Cast ${spellName}`, // @date_of_change 2025-09-14 - Fixed: Use spellName instead of spellId to prevent [object Object]
        castingTime: isFullSpellObject ? spellData.castingTime : '1 action',
        range: spellRange,
        components: isFullSpellObject ? spellData.components : 'V, S',
        duration: isFullSpellObject ? spellData.duration : 'Instantaneous',
        level: isFullSpellObject ? spellData.level : (source === 'cantrips' || source === 'level0' ? 0 : 1),
        school: spellSchool,
        // CRITICAL: Add proper targeting and action type classification
        targetingType: targetingType, // 'self', 'ally', 'enemy'
        actionType: actionType, // 'action', 'bonus_action', 'reaction'
        // Store original spell data for detailed display
        spellData: spellData,
        // @date_of_change 2025-09-13 - FULL EMBED: Include damage data if present
        damage: isFullSpellObject ? spellData.damage : undefined,
        // Runtime combat must consume the fully embedded canonical mechanic verbatim.
        // It must never infer healing dice from a spell name or description.
        // date_of_change: 2026-08-31
        healing: isFullSpellObject ? spellData.healing : undefined,
        save: isFullSpellObject ? spellData.save : undefined,
        // @date_of_change 2025-09-22 - CRITICAL FIX: Preserve area property for AoE targeting
        // The working character has 'area' not 'aoe', need to check both property names
        area: isFullSpellObject ? (spellData.area || spellData.aoe) : undefined,
        aoe: isFullSpellObject ? spellData.aoe : undefined
      };

      // Add spell attack or save info if available
      if (spellData?.attack) {
        spellAction.attack = spellData.attack;
      }
      if (spellData?.save) {
        spellAction.save = spellData.save;
      }

      debugLog(`[deriveActionsFromCharacterData] Classified spell: ${spellName} - targeting: ${targetingType}, actionType: ${actionType}, school: ${spellSchool}`);

      actions.push(spellAction);
      });
    

    // 3. CLASS FEATURES - Convert usable features to special actions
    if (Array.isArray(featuresData)) {
      featuresData.forEach(feature => {
        // Only include features that have limited uses or are explicitly combat actions
        const hasLimitedUses = feature.uses && feature.uses.max > 0;
        const isCombatFeature = feature.name?.toLowerCase().includes('attack') || 
                               feature.name?.toLowerCase().includes('strike') ||
                               feature.name?.toLowerCase().includes('rage') ||
                               feature.name?.toLowerCase().includes('channel') ||
                               feature.description?.toLowerCase().includes('action') ||
                               feature.description?.toLowerCase().includes('bonus action');
        
        if (hasLimitedUses || isCombatFeature) {
          const featureAction = {
            name: feature.name || 'Unknown Feature',
            type: 'feature',
            category: feature.uses?.resetOn === 'short rest' ? 'short-rest' : 
                      feature.uses?.resetOn === 'long rest' ? 'long-rest' : 'special',
            description: feature.description || 'A special class feature',
            source: feature.source || 'Class Feature',
            uses: feature.uses ? {
              current: feature.uses.current || feature.uses.max || 1,
              max: feature.uses.max || 1,
              resetOn: feature.uses.resetOn || 'long rest'
            } : null,
            // Store original feature data for detailed display
            featureData: feature
          };
          actions.push(featureAction);
        }
      });
    }

    debugLog('[deriveActionsFromCharacterData] Derived actions:', {
      totalActions: actions.length,
      weaponActions: actions.filter(a => a.type === 'weapon').length,
      spellActions: actions.filter(a => a.type === 'spell').length,
      featureActions: actions.filter(a => a.type === 'feature').length,
      actionNames: actions.map(a => a.name)
    });
    
    return actions;
  }, []);

  const createTokenFromDashboardData = useCallback((entityData, targetLayer = 'token', options = {}) => {
    debugLog('[createTokenFromDashboardData] Creating token from entity data:', entityData);

    // 🔍 DEBUG - Token Creation Input Phase
    debugLog('[createTokenFromDashboardData] Token creation receiving:', {
      name: entityData.name,
      type: entityData.type,
      actions: entityData.actions,
      actionsCount: entityData.actions?.length || 0,
      hp: entityData.hp,
      ac: entityData.ac,
      abilities: entityData.abilities,
      allKeys: Object.keys(entityData),
      hasMonsterData: !!entityData.monsterData,
      fullInputData: entityData
    });

    // Roster clicks are serialized through acknowledgement AND rendered state.
    // A pending actor is never published as a successful local placement, and
    // the next placement sees the committed footprint of its predecessor.
    // Rule: session snapshots own actor membership. date_of_change: 2026-11-05
    const scope = rosterScopeRef.current;
    const assertPlacementScope = () => {
      const current = rosterScopeRef.current;
      if (!rosterMountedRef.current || current.sessionId !== scope.sessionId || current.sceneId !== scope.sceneId || current.multiplayer !== scope.multiplayer) {
        throw new Error('Token placement cancelled because the BattleRoom or active scene changed. Add the actor again in the intended scene.');
      }
    };
    const createToken = async () => {
      assertPlacementScope();
      if (isMultiplayerMode) {
        const revision = await waitForBattleRoomMutationQueue();
        await waitForAuthoritativeRevision(revision);
        assertPlacementScope();
      }
      try {
        // The asset browser can add the next actor before React commits the
        // previous render. Keep a synchronously advanced collection so each
        // placement sees every actor already queued in this interaction burst.
        // date_of_change: 2026-08-18
        const currentTokens = [...tokensRef.current];
        const placementHint = options?.placementHint;
        // DEBUG: Log raw input data structure to understand nesting issues
        debugLog('[createTokenFromDashboardData] Raw entityData:', JSON.stringify(entityData, null, 2));
        
        debugLog('[createTokenFromDashboardData] Creating token from data:', {
          name: entityData.name,
          type: entityData.type || 'unknown',
          targetLayer,
          hasHP: Boolean(entityData.hp),
          isCustom: entityData.isCustom || false
        });

        // STEP 1: Basic token creation with common properties
        const tokenId = uuidv4();

        // @date_of_change 2025-09-19 - Find unoccupied space for new tokens
        // Use imported movement utilities for intelligent token placement

        // Determine token size early for space calculation
        const sizeCategory = entityData.size || entityData.schemaData?.size || 'Medium';
        const tokenSizeInSquares = getTokenSizeFromCategory(sizeCategory) / gridSize;

        // Find an unoccupied space starting from the visible viewport when available.
        const defaultPosition = (
          placementHint &&
          Number.isFinite(placementHint.x) &&
          Number.isFinite(placementHint.y)
        ) ? {
          x: Math.max(Math.round((placementHint.x / gridSize) - (tokenSizeInSquares / 2)), 0),
          y: Math.max(Math.round((placementHint.y / gridSize) - (tokenSizeInSquares / 2)), 0)
        } : { x: 2, y: 2 };
        const placementSearch = buildReadableTokenPlacementSearch({
          gridSize,
          viewScale: placementHint?.viewScale,
          gridWidth,
          gridHeight,
          tokenSize: tokenSizeInSquares
        });
        const unoccupiedGridPos = findNearestUnoccupiedSpace(
          defaultPosition,
          currentTokens,
          gridSize,
          placementSearch.maxSearchRadius,
          tokenSizeInSquares,
          placementSearch.searchOptions
        );

        // Convert grid coordinates back to pixel coordinates
        const pixelPosition = {
          x: unoccupiedGridPos.x * gridSize,
          y: unoccupiedGridPos.y * gridSize
        };

        debugLog(`[createTokenFromDashboardData] Placing ${entityData.name} at unoccupied position:`, {
          requested: defaultPosition,
          found: unoccupiedGridPos,
          pixelPos: pixelPosition,
          tokenSize: tokenSizeInSquares
        });

        // @date_of_change: 2025-10-07 - Determine token type for default ownership
        const tokenType = (entityData.type || (entityData.creatureType ? 'monster' : 'character') || 'character').toLowerCase();

        const newToken = {
          id: tokenId,
          name: entityData.name || 'Unknown Entity',
          // Normalize to lowercase to align with UI checks (e.g., 'monster')
          type: tokenType,
          layer: targetLayer,
          x: pixelPosition.x,
          y: pixelPosition.y,
          draggable: getAssetDraggable(targetLayer),
          isCustom: entityData.isCustom || false
        };

        // @date_of_change: 2025-10-07 - Default ownership assignment
        // Characters default to 'all' (all players), Monsters default to 'gm' (GM only)
        if (tokenType === 'monster') {
          newToken.controlType = 'gm';
          newToken.controlledBy = null;
        } else {
          newToken.controlType = 'all';
          newToken.controlledBy = null;
        }

        // @date_of_change: 2025-09-30 - Artwork propagation for unique per-entity images
        // @date_of_change: 2025-10-07 - Keep tokenAssetKey with leading '/uploads/' for ArtworkResolver
        // Copy uploaded key from the entity (if present) and add a lightweight entity hint
        if (entityData?.tokenAssetKey) {
          newToken.tokenAssetKey = String(entityData.tokenAssetKey);
        }
        // @date_of_change: 2025-10-08 - Preserve artwork hints for resolver
        // Why: Custom entities may only have 'artwork' (no tokenAssetKey). Token.js calls
        // resolveCharacterArtUrl/resolveMonsterArtUrl with token.entity, so we must keep
        // those hint fields. Also mirror tokenAssetKey into tokenAsset.key for legacy
        // hint compatibility used by ArtworkResolver.
        newToken.entity = {
          id: entityData.id,
          name: entityData.name,
          class: entityData.class,
          type: entityData.type,
          // Prefer uploaded key if provided, enables CDN URL resolution in Token.js
          tokenAssetKey: newToken.tokenAssetKey || entityData.tokenAssetKey || null,
          ...(newToken.tokenAssetKey ? { tokenAsset: { key: String(newToken.tokenAssetKey).replace(/^\//, '') } } : {}),
          // Hint fields used by resolver fallbacks
          artwork: entityData.artwork || entityData.portrait || entityData.image || entityData.imageKey || entityData.artKey || null,
        };
        // @date_of_change 2025-10-12 - Guard against double-nesting canonical payloads
        if (entityData.entity || entityData.token || entityData.data?.token || entityData.canonical?.token) {
          console.warn('[createTokenFromDashboardData] ⚠️ Nested token structure detected in entityData. Storing minimal resolver hints only to avoid duplication.', {
            entityKeys: Object.keys(entityData || {}),
            sample: {
              entity: entityData.entity,
              token: entityData.token,
              dataToken: entityData.data?.token,
              canonicalToken: entityData.canonical?.token,
            }
          });
        }
        debugLog('[createTokenFromDashboardData] Artwork hints on token:', {
          tokenId: newToken.id,
          tokenAssetKey: newToken.tokenAssetKey,
          entityHint: newToken.entity
        });
        // date_of_change: 2025-10-18 - SSOT: Convert entity to token core via TokenAdapter
        const core = TokenAdapter.toTokenCore(entityData);
        Object.assign(newToken, core);

        // STEP 3: Set proper size based on entity size category (presentation concern)
        const tokenSize = getTokenSizeFromCategory(sizeCategory);
        newToken.width = tokenSize;
        newToken.height = tokenSize;
        newToken.size = sizeCategory;

        // STEP 4: Apply grid snapping and compute grid coordinates (presentation concern)
        if (snapToGrid) {
          const snappedPos = snapToGridPosition(newToken.x, newToken.y);
          newToken.x = snappedPos.x;
          newToken.y = snappedPos.y;
        }
        const initialGridCoords = pixelToGrid(newToken.x, newToken.y, gridSize);
        // The authoritative movement service consumes the same structured
        // footprint that rendering uses. Supply it at token creation so a
        // freshly placed asset is immediately movable after snapshot
        // compaction. date_of_change: 2026-08-25
        newToken.grid = { x: initialGridCoords.x, y: initialGridCoords.y };
        newToken.gridX = initialGridCoords.x;
        newToken.gridY = initialGridCoords.y;
        newToken.sizeCells = { w: tokenSizeInSquares, h: tokenSizeInSquares };

        // STEP 5: Character-specific enhancements (shared action economy/catalog)
        if (newToken.type !== 'monster') {
          const derivedActions = deriveActionsFromCharacterData(entityData);
          const normalizedActionEconomy = buildCombatActionEconomy(entityData);
          const hasNormalizedActionEconomy = Boolean(
            normalizedActionEconomy?.actions?.length
            || normalizedActionEconomy?.bonusActions?.length
            || normalizedActionEconomy?.reactions?.length
          );
          if (!newToken.characterData) newToken.characterData = {};
          if (hasNormalizedActionEconomy) {
            newToken.characterData.actions = normalizedActionEconomy;
            newToken.characterData.bonusActions = normalizedActionEconomy.bonusActions;
            newToken.characterData.reactions = normalizedActionEconomy.reactions;
          }
          newToken.characterData.derivedActions = derivedActions;
        }

        // @date_of_change: 2025-09-29 - Use ArtworkResolver for consistent CDN paths and fallbacks
        // @date_of_change: 2025-10-07 - Do not call async resolveUploadedKey here; synchronous resolvers handle tokenAssetKey
        const normalizedType = (entityData.type || '').toString().toLowerCase();
        let tokenImageSrc;
        try {
          if (normalizedType === 'monster') {
            tokenImageSrc = resolveMonsterArtUrl(entityData);
          } else {
            tokenImageSrc = resolveCharacterArtUrl(entityData);
          }
        } catch (e) {
          tokenImageSrc = resolveCharacterArtUrl(entityData);
        }

        debugLog('[createTokenFromDashboardData] Using ArtworkResolver:', {
          entityName: entityData.name,
          entityType: entityData.type,
          isCustom: entityData.isCustom,
          hasArtwork: !!entityData.artwork,
          tokenAssetKey: entityData.tokenAssetKey,
          resolvedImageSrc: tokenImageSrc
        });

        // Set the token image source using resolver result
        newToken.src = tokenImageSrc;

        // @date_of_change: 2025-10-03 - Fix race condition by explicitly passing updated tokens array
        // Add token to the current array captured at the start
        const updatedTokens = [...currentTokens, newToken];

        // Live membership is applied only from the server snapshot. Local-only
        // rooms continue to own their collection directly. date_of_change: 2026-11-05
        if (isMultiplayerMode) {
          const acknowledgement = await sendBattleRoomMutation('updateMap', { mapState: {
            ...serializeRosterAddition(newToken),
            updateId: `token_add_${Date.now()}_${Math.random()}`
          } });
          await waitForAuthoritativeRevision(acknowledgement?.revision);
          assertPlacementScope();
          if (!tokensRef.current.some(token => token.id === newToken.id)) {
            throw new Error(`${newToken.name} was not present in the acknowledged BattleRoom roster.`);
          }
        } else {
          tokensRef.current = updatedTokens;
          setTokens(updatedTokens);
        }

        debugLog('[createTokenFromDashboardData] Token created successfully:', {
          id: newToken.id,
          name: newToken.name,
          type: newToken.type,
          hp: newToken.hp,
          abilities: newToken.abilities,
          hasMonsterData: !!newToken.monsterData,
          hasCharacterData: !!newToken.characterData
        });
        
        if (unoccupiedGridPos.labelClearanceReduced) {
          options.onPlacementNotice?.(`${newToken.name} was placed in an open space. Zoom in to separate overlapping name labels.`);
        }
        return newToken;
      } catch (error) {
        console.error('[createTokenFromDashboardData] Error creating token:', error);
        throw error;
      }
    };
    const creation = tokenCreationQueueRef.current.then(createToken);
    // The caller still receives rejection. Settle this queue link so one
    // rejected placement does not permanently strand subsequent clicks.
    tokenCreationQueueRef.current = creation.catch(() => undefined);
    return creation;
  }, [gridHeight, gridSize, gridWidth, snapToGrid, snapToGridPosition, getTokenSizeFromCategory, getAssetDraggable, setTokens, deriveActionsFromCharacterData, isMultiplayerMode, sendBattleRoomMutation, waitForBattleRoomMutationQueue, waitForAuthoritativeRevision, pixelToGrid]);

  /**
   * Update token position with grid coordinate calculation
   * 
   * This function updates both pixel coordinates and grid coordinates
   * when a token is moved. Grid coordinates treat each grid square as
   * one coordinate unit, with (0,0) at the upper left.
   * 
   * @param {string} tokenId - ID of the token to update
   * @param {number} x - New X pixel position
   * @param {number} y - New Y pixel position
   * @param {object} additionalProps - Any additional properties to update
   */
  const updateTokenPosition = useCallback((tokenId, x, y, additionalProps = {}) => {
    // Apply grid snapping if enabled
    const snappedPos = snapToGridPosition(x, y);
    
    // Calculate grid coordinates (0,0 at upper left)
    const gridCoords = pixelToGrid(snappedPos.x, snappedPos.y, gridSize);
    const effectiveGridBounds = getEffectiveBattleGridBounds({
      mapSize: mapState?.size,
      gridSize,
      gridWidth,
      gridHeight
    });
    
    // Check if position is within grid bounds
    const isWithinBounds =
      gridCoords.x >= 0 &&
      gridCoords.x < effectiveGridBounds.columns &&
      gridCoords.y >= 0 &&
      gridCoords.y < effectiveGridBounds.rows;

    const newProperties = {
      x: snappedPos.x,
      y: snappedPos.y,
      grid: isWithinBounds ? { x: gridCoords.x, y: gridCoords.y } : null,
      gridX: isWithinBounds ? gridCoords.x : 'off grid',
      gridY: isWithinBounds ? gridCoords.y : 'off grid',
      ...additionalProps
    };
    
    handleTokenUpdate(tokenId, newProperties);
  }, [snapToGridPosition, pixelToGrid, gridSize, gridWidth, gridHeight, handleTokenUpdate, mapState?.size]);

  /**
   * Utility function to check if coordinates are within grid bounds
   */
  const isWithinGridBounds = useCallback((x, y) => {
    const effectiveGridBounds = getEffectiveBattleGridBounds({
      mapSize: mapState?.size,
      gridSize,
      gridWidth,
      gridHeight
    });
    return x >= 0 && x <= effectiveGridBounds.pixelWidth && y >= 0 && y <= effectiveGridBounds.pixelHeight;
  }, [gridWidth, gridHeight, gridSize, mapState?.size]);

  /**
   * Utility function to get grid coordinates with bounds checking
   */
  const getGridCoordinates = useCallback((x, y) => {
    if (!isWithinGridBounds(x, y)) {
      return 'off grid';
    }
    const gridX = Math.floor(x / gridSize);
    const gridY = Math.floor(y / gridSize);
    return `${gridX}, ${gridY}`;
  }, [gridSize, isWithinGridBounds]);

  /**
   * Auto-scale all tokens when grid size changes
   */
  useEffect(() => {
    // Avoid cascading updates during session load
    if (isLoadingSessionRef.current) return;

    // Batch autoscaling into a single state update to prevent multiple re-renders
    setTokens(prevTokens => {
      if (!prevTokens || prevTokens.length === 0) return prevTokens;
      let changed = false;
      const next = prevTokens.map(token => {
        if (token.type !== 'map' && token.type !== 'object') {
          const newSize = getTokenSizeFromCategory(token.size || 'Medium');
          const newSizeInCells = newSize / gridSize;
          if (
            token.width !== newSize
            || token.height !== newSize
            || token.sizeCells?.w !== newSizeInCells
            || token.sizeCells?.h !== newSizeInCells
          ) {
            changed = true;
            // Keep pixel and cell footprints atomic when the grid scale changes;
            // authoritative bounds checks must match the rendered token.
            // date_of_change: 2026-08-25
            return { ...token, width: newSize, height: newSize, sizeCells: { w: newSizeInCells, h: newSizeInCells } };
          }
        }
        return token;
      });
      return changed ? next : prevTokens;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridSize, getTokenSizeFromCategory]);

  /**
   * Auto-scale token when its size property changes
   */
  const handleTokenSizeChange = useCallback((tokenId, newSizeCategory) => {
    const newSize = getTokenSizeFromCategory(newSizeCategory);
    const newSizeInCells = newSize / gridSize;
    const newProperties = {
      size: newSizeCategory,
      width: newSize,
      height: newSize,
      sizeCells: { w: newSizeInCells, h: newSizeInCells }
    };
    handleTokenUpdate(tokenId, newProperties);
  }, [getTokenSizeFromCategory, gridSize, handleTokenUpdate]);

  const handleDragEnd = useCallback((e, tokenId) => {
    const rawX = e.target.x();
    const rawY = e.target.y();
    
    // Use the new updateTokenPosition function that handles both pixel and grid coordinates
    updateTokenPosition(tokenId, rawX, rawY);
  }, [updateTokenPosition]);

  // Grid control handlers
  const handleGridToggle = useCallback(() => {
    const newValue = !showGrid;
    setShowGrid(newValue);
    localStorage.setItem('battleRoomShowGrid', newValue);
  }, [showGrid]);

  const handleSnapToggle = useCallback(() => {
    const newValue = !snapToGrid;
    setSnapToGrid(newValue);
    localStorage.setItem('battleRoomSnapToGrid', newValue);
  }, [snapToGrid]);

  // Zoom control handlers
  const handleZoomIn = useCallback(() => {
    setZoom(prevZoom => normalizeBattleRoomZoom(prevZoom * 1.2, 1));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prevZoom => normalizeBattleRoomZoom(prevZoom / 1.2, 1));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(normalizeBattleRoomZoom(1, 1));
  }, []);

  // Fog of War handlers
  // date_of_change: 2025-09-24 - Minimal fog controls (enable/disable, opacity, reveal/cover all)
  const toggleFogEnabled = useCallback(() => {
    setFogOfWar(prev => {
      const next = { ...prev, enabled: !prev.enabled };
      localStorage.setItem('battleRoomFogEnabled', String(next.enabled));
      // @date_of_change: 2025-10-03 - Sync fog changes to multiplayer
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  const setFogOpacity = useCallback((newOpacity) => {
    const op = Math.min(Math.max(parseFloat(newOpacity) || 0.85, 0.1), 1);
    setFogOfWar(prev => {
      const next = { ...prev, opacity: op };
      localStorage.setItem('battleRoomFogOpacity', String(op));
      // @date_of_change: 2025-10-03 - Sync fog changes to multiplayer
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  const toggleFogIgnoreGM = useCallback(() => {
    setFogOfWar(prev => {
      const next = { ...prev, ignoreGM: !prev.ignoreGM };
      localStorage.setItem('battleRoomFogIgnoreGM', String(next.ignoreGM));
      // @date_of_change: 2025-10-03 - Sync fog changes to multiplayer
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  /**
   * Add a normalized rectangle operation to fog (rectPct fields are 0..1 relative to map area)
   */
  const addFogOperationRectNormalized = useCallback((type, rectPct) => {
    if (!rectPct || !['reveal', 'cover'].includes(type)) return;
    setFogOfWar(prev => {
      const next = {
        ...prev,
        operations: [...prev.operations, { type, rectPct }]
      };
      // @date_of_change: 2025-10-03 - Sync fog changes to multiplayer
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  /**
   * GAP-003: Add a polygon or lasso operation to fog.
   * Points are stored as normalized {xPct, yPct} pairs (0..1 relative to map area).
   * Ramer-Douglas-Peucker simplification is applied to lasso points to prevent
   * dense point clouds from degrading canvas performance.
   * Rule reference: GAP-003 spec — Operations stored as normalized geometry, not raster.
   * date_of_change: 2026-05-20
   * @param {'reveal'|'cover'} type
   * @param {'polygon'|'lasso'} shape - 'polygon' for click-to-place vertices, 'lasso' for freehand
   * @param {{ xPct: number, yPct: number }[]} pointsPct - Normalized point array (min 3 points)
   */
  const addFogOperationPolygon = useCallback((type, shape, pointsPct) => {
    if (!['reveal', 'cover'].includes(type)) return;
    if (!['polygon', 'lasso'].includes(shape)) return;
    if (!Array.isArray(pointsPct) || pointsPct.length < 3) return;

    // RDP simplification: tolerance in normalized units (0.002 ≈ 0.2% of map width)
    const simplified = shape === 'lasso'
      ? rdpSimplify(pointsPct, 0.002)
      : pointsPct;

    if (simplified.length < 3) return;

    setFogOfWar(prev => {
      const next = {
        ...prev,
        operations: [...prev.operations, { type, shape, pointsPct: simplified }]
      };
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  const clearFogOperations = useCallback(() => {
    setFogOfWar(prev => {
      const next = { ...prev, operations: [] };
      // @date_of_change: 2025-10-03 - Sync fog changes to multiplayer
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  /**
   * GAP-003: Undo the last fog operation (removes final entry from operations array).
   * Rule reference: GAP-003 spec — operations stored as normalized geometry list.
   * date_of_change: 2026-05-20
   */
  const undoLastFogOperation = useCallback(() => {
    setFogOfWar(prev => {
      if (!prev.operations || prev.operations.length === 0) return prev;
      const next = { ...prev, operations: prev.operations.slice(0, -1) };
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  const revealAllFog = useCallback(() => {
    setFogOfWar(prev => {
      const next = {
        ...prev,
        operations: [{ type: 'reveal', rectPct: { xPct: 0, yPct: 0, wPct: 1, hPct: 1 } }]
      };
      // @date_of_change: 2025-10-03 - Sync fog changes to multiplayer
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  const coverAllFog = useCallback(() => {
    // Base mask covers everything; clearing operations resets to full cover
    setFogOfWar(prev => {
      const next = { ...prev, operations: [] };
      // @date_of_change: 2025-10-03 - Sync fog changes to multiplayer
      syncStateToMultiplayer('fog_update', next);
      return next;
    });
  }, [syncStateToMultiplayer]);

  /**
   * Session save handler - Saves to local storage AND offers download option
   * Saves all maps, tokens, layers, grid settings, and zoom level
   * @param {boolean} overwrite - Whether to overwrite existing sessions if storage is full
   * @param {string} sessionIdToOverwrite - Optional specific session ID to overwrite
   */
  const handleSaveSession = useCallback(async (overwrite = false, sessionIdToOverwrite = null, battleRoomState = {}) => {
    console.log('[handleSaveSession] Starting save session process', { overwrite, sessionIdToOverwrite });
    try {
      // Ensure all token positions are up-to-date by flushing any pending state updates
      // This ensures we're not saving stale position data that might be in the middle of updating
      const currentTokens = [...tokens].map(token => ({ ...token }));
      // DEBUG: Log comprehensive token data before saving session
      console.log('[handleSaveSession] Token data before save:', tokens.map(token => ({
        id: token.id,
        name: token.name,
        // Position data
        x: token.x,
        y: token.y,
        gridX: token.gridX,
        gridY: token.gridY,
        // Type info
        type: token.type,
        layer: token.layer,
        // Combat stats
        initiative: token.initiative,
        hp: token.hp,
        maxHp: token.maxHp,
        hpObj: token.hpObj,
        ac: token.ac,
        // Status/conditions
        conditions: token.conditions,
        statusEffects: token.statusEffects,
        // Spell tracking
        spells: token.spells ? { 
          slots: token.spells.slots,
          prepared: token.spells.prepared?.length,
          used: token.spells.used
        } : undefined,
        // Resources tracking
        resources: token.resources
      })));
      
      // Create a deep clone of the battle state to prevent serialization issues with circular references
      // We specifically create a clean copy of tokens to ensure position data is preserved exactly
      const deepClonedTokens = currentTokens.map(token => {
        // Create a clean copy of the token with only the essential data
        const cleanToken = {
          ...token,
          // Position data - critically important to preserve exactly
          x: Number(token.x) || 0,  // Ensure numeric
          y: Number(token.y) || 0,  // Ensure numeric
          gridX: token.gridX || 0,  // Might be numeric or 'off grid'
          gridY: token.gridY || 0,  // Might be numeric or 'off grid'
          // Other battle state
          initiative: Number(token.initiative) || 0
        };
        
        // Ensure HP structure is valid
        if (token.hpObj) {
          cleanToken.hpObj = {
            current: Number(token.hpObj.current) || 0,
            max: Number(token.hpObj.max) || 0
          };
          // Keep legacy fields in sync
          cleanToken.hp = cleanToken.hpObj.current;
          cleanToken.maxHp = cleanToken.hpObj.max;
        } else if (token.hp && typeof token.hp === 'object' && token.hp.current !== undefined) {
          // Legacy tokens that stored hp as an object in `hp`
          cleanToken.hpObj = {
            current: Number(token.hp.current) || 0,
            max: Number(token.hp.max) || 0
          };
          cleanToken.hp = cleanToken.hpObj.current;
          cleanToken.maxHp = cleanToken.hpObj.max;
        } else if (typeof token.hp === 'number' || typeof token.maxHp === 'number') {
          // Pure numeric legacy fields
          cleanToken.hpObj = {
            current: Number(token.hp) || 0,
            max: Number(token.maxHp) || 0
          };
          cleanToken.hp = cleanToken.hpObj.current;
          cleanToken.maxHp = cleanToken.hpObj.max;
        }
        
        // Ensure conditions and status effects are arrays/objects
        cleanToken.conditions = Array.isArray(token.conditions) ? [...token.conditions] : [];
        cleanToken.statusEffects = token.statusEffects ? { ...token.statusEffects } : {};
        
        // Handle spell slots and tracking
        if (token.spells) {
          cleanToken.spells = {
            slots: token.spells.slots ? { ...token.spells.slots } : {},
            prepared: Array.isArray(token.spells.prepared) ? [...token.spells.prepared] : [],
            used: token.spells.used ? { ...token.spells.used } : {}
          };
        }
        
        // Resources tracking
        if (token.resources) {
          cleanToken.resources = { ...token.resources };
        }

        // @date_of_change 2025-09-16 - CRITICAL FIX: Preserve character/monster data during session save
        // This ensures monster actions and character features are restored correctly
        if (token.characterData) {
          cleanToken.characterData = { ...token.characterData };
        }
        if (token.monsterData) {
          cleanToken.monsterData = { ...token.monsterData };
        }

        // Preserve essential character/monster action data directly on token for backward compatibility
        if (token.actions) {
          cleanToken.actions = Array.isArray(token.actions) ? [...token.actions] : { ...token.actions };
        }
        if (token.classFeatures) {
          cleanToken.classFeatures = [...token.classFeatures];
        }
        if (token.items) {
          cleanToken.items = [...token.items];
        }
        if (token.feats) {
          cleanToken.feats = [...token.feats];
        }
        if (token.legendaryActions) {
          cleanToken.legendaryActions = [...token.legendaryActions];
        }
        if (token.specialAbilities) {
          cleanToken.specialAbilities = [...token.specialAbilities];
        }

        return cleanToken;
      });
      
      // @date_of_change 2025-09-15 - CRITICAL ENHANCEMENT: Complete combat state preservation
      // Get complete combat log state using proper export method
      const combatLogState = CombatLogManager ? CombatLogManager.exportState() : null;

      // Get action economy state using proper export method
      const actionEconomyState = ActionEconomyManager ? ActionEconomyManager.exportState() : null;

      // Create a session object with the complete battle state including all combat data
      const sessionData = {
        version: '2.0', // Updated version to reflect enhanced combat data preservation
        timestamp: new Date().toISOString(),
        battleState: {
          // Core battle map data
          tokens: deepClonedTokens,
          mapImage,
          mapState,
          mapImages,
          sceneAssets,
          selectedTokenId,

          // Grid and zoom settings
          showGrid,
          snapToGrid,
          gridSize,
          gridOpacity,
          gridWidth,
          gridHeight,
          zoom,

          // Fog of War settings
          // date_of_change: 2025-09-24 - Persist fog-of-war state and operations for session restoration
          fogOfWar,

          // GAP-002: Drawing layer — persist completed strokes for session restoration
          // date_of_change: 2026-05-20
          drawings,

          // Layer configuration
          activeLayer,
          layers
        },

        // @date_of_change 2025-09-15 - NEW: Complete combat state preservation
        combatState: {
          // Combat log with complete history
          combatLog: combatLogState,

          // Action economy tracking
          actionEconomy: actionEconomyState,

          // Initiative order and turn tracking (extracted from tokens for easier restoration)
          initiativeOrder: deepClonedTokens
            .filter(token => (
              token.type !== 'map'
              && token.type !== 'object'
              && Number.isFinite(Number(token.initiative))
            ))
            .sort((a, b) => (b.initiative || 0) - (a.initiative || 0))
            .map(token => ({
              id: token.id,
              name: token.name,
              initiative: token.initiative,
              type: token.type
            })),

          // Current combatant and turn state
          currentCombatantId: selectedTokenId, // Currently selected token represents current combatant

          // Combat flags and metadata
          combatStarted: CombatLogManager ? CombatLogManager.combatStarted : false,
          currentRound: CombatLogManager ? CombatLogManager.currentRound : 1,
          currentTurn: CombatLogManager ? CombatLogManager.currentTurn : 0,

          // @date_of_change 2025-09-15 - NEW: BattleRoom state preservation
          // BattleRoom-specific combat state variables
          battleRoomState: {
            actionsUsed: battleRoomState.actionsUsed || {},
            armedAction: battleRoomState.armedAction || null,
            selectedTargets: battleRoomState.selectedTargets || [],
            multiattackSequence: battleRoomState.multiattackSequence || null,
            isInMultiattackMode: battleRoomState.isInMultiattackMode || false,
            multiattackTarget: battleRoomState.multiattackTarget || null,
            targetingEnabled: battleRoomState.targetingEnabled || false,
            currentCombatant: battleRoomState.currentCombatant || null
          }
        }
      };
      
      // @date_of_change 2025-09-15 - Enhanced debug logging for comprehensive session data
      console.log('[handleSaveSession] 📊 ENHANCED SESSION DATA SUMMARY:');
      console.log('[handleSaveSession] Session version:', sessionData.version);
      console.log('[handleSaveSession] Token count:', sessionData.battleState.tokens.length);
      console.log('[handleSaveSession] Combat started:', sessionData.combatState.combatStarted);
      console.log('[handleSaveSession] Combat log entries:', sessionData.combatState.combatLog?.combatLog?.length || 0);
      console.log('[handleSaveSession] Initiative order entries:', sessionData.combatState.initiativeOrder?.length || 0);
      console.log('[handleSaveSession] Current round:', sessionData.combatState.currentRound);
      console.log('[handleSaveSession] Current turn:', sessionData.combatState.currentTurn);
      console.log('[handleSaveSession] Current combatant ID:', sessionData.combatState.currentCombatantId);

      // DEBUG: Verify comprehensive token data is correctly included in the session data
      if (sessionData.battleState.tokens && sessionData.battleState.tokens.length > 0) {
        console.log('[handleSaveSession] Token data in session data:',
          sessionData.battleState.tokens.map(token => ({
            id: token.id,
            name: token.name,
            // Position data
            x: token.x,
            y: token.y,
            gridX: token.gridX,
            gridY: token.gridY,
            // Combat stats
            initiative: token.initiative,
            hp: token.hp !== undefined ? 'present' : 'missing',
            maxHp: token.maxHp !== undefined ? 'present' : 'missing',
            hpObj: token.hpObj ? 'present' : 'missing',
            ac: token.ac,
            // Status/conditions
            conditions: token.conditions ? token.conditions.length : 0,
            statusEffects: token.statusEffects ? Object.keys(token.statusEffects).length : 0,
            // Spell tracking
            spellsTracked: token.spells ? true : false,
            // Resources tracking
            resourcesTracked: token.resources ? true : false
          })))
      }
      
      console.log('[handleSaveSession] Session data prepared, calling SessionManager.saveSession');
      // @date_of_change 2025-09-16 - PRODUCTION FILE-BASED STORAGE: Handle async saveSession
      const savedSession = await SessionManager.saveSession(sessionData, null, overwrite, sessionIdToOverwrite);
      console.log('[handleSaveSession] SessionManager.saveSession returned:', savedSession);

      if (!savedSession) {
        throw new Error('SessionManager.saveSession returned no data');
      }

      // Session is now automatically saved as a file download - inform user
      window.alert(
        `Session Saved! 🎉\n\n"${savedSession.name}" has been downloaded to your computer.\n\nThe session file will also appear on your Dashboard for easy access.`
      );
      
      console.log('[handleSaveSession] Battle session saved successfully:', savedSession.name);
      
    } catch (error) {
      console.error('[handleSaveSession] Failed to save session:', error);
      console.error('[handleSaveSession] Error details:', { name: error.name, message: error.message, stack: error.stack });
      
      // Check if we have a recoverable session that can be downloaded
      if (error.recoverableSession) {
        const shouldDownloadRecovered = window.confirm(
          `Unable to save to browser storage: ${error.message}\n\n` +
          'Would you like to download this session instead?'
        );
        
        if (shouldDownloadRecovered) {
          try {
            console.log('[handleSaveSession] Attempting to download recovered session');
            SessionManager.downloadSession(error.recoverableSession);
            return; // Exit early if download successful
          } catch (downloadError) {
            console.error('[handleSaveSession] Failed to download recovered session:', downloadError);
          }
        }
      }
      
      // Show appropriate error message based on error type
      if (error.message?.includes('storage is full')) {
        alert('Your browser storage is full. Please delete some old sessions or use the download option.');
      } else if (error.message?.includes('security settings')) {
        alert('Unable to save due to browser security settings. Please check that cookies and local storage are enabled.');
      } else if (error.message?.includes('private browsing')) {
        alert('Unable to save in private browsing mode. Please use the download option instead.');
      } else {
        alert(`Failed to save session: ${error.message || 'Unknown error'}. Please try again or use the download option.`);
      }
    }
  }, [mapImage, mapState, mapImages, sceneAssets, tokens, selectedTokenId,
      showGrid, snapToGrid, gridSize, gridOpacity, gridWidth, gridHeight, zoom, activeLayer, layers, fogOfWar,
      drawings]); // GAP-002: drawings included per exhaustive-deps — date_of_change: 2026-05-20

  /**
   * Direct session loading from data object (used by dashboard)
   * @param {Object} sessionData - Pre-parsed session data object
   */
  const loadSessionFromData = useCallback((sessionData, options = {}) => {
    const sessionSignature = buildBattleRoomSessionLoadSignature(sessionData);
    const forceRestore = options?.forceRestore === true;

    if (shouldSkipBattleRoomSessionRestore({
      forceRestore,
      isLoading: isLoadingSessionRef.current,
      lastSignature: lastLoadedSessionSignatureRef.current,
      nextSignature: sessionSignature
    })) {
      console.log('[loadSessionFromData] Skipping duplicate session restore for signature:', sessionSignature);
      return {
        success: true,
        battleRoomState:
          sessionData?.combatState?.battleRoomState
          || sessionData?.data?.combatState?.battleRoomState
          || null
      };
    }

    try {
      // Suppress cascading effects during bulk state restoration
      isLoadingSessionRef.current = true;
      lastLoadedSessionSignatureRef.current = sessionSignature;

      // @date_of_change 2025-09-16 - BACKWARD COMPATIBILITY: Handle different session data formats
      console.log('[loadSessionFromData] 🔍 DEBUGGING SESSION DATA STRUCTURE:');
      console.log('[loadSessionFromData] Session data keys:', Object.keys(sessionData));
      console.log('[loadSessionFromData] Session data type:', typeof sessionData);
      console.log('[loadSessionFromData] Has battleState:', !!sessionData.battleState);
      console.log('[loadSessionFromData] Has data.battleState:', !!(sessionData.data && sessionData.data.battleState));
      console.log('[loadSessionFromData] Has tokens:', !!sessionData.tokens);
      console.log('[loadSessionFromData] Has version:', sessionData.version);
      console.log('[loadSessionFromData] Raw session data sample:', JSON.stringify(sessionData, null, 2).substring(0, 500));

      let battleState, combatState;

      if (sessionData.data && sessionData.data.battleState) {
        // Nested format (current save format) - data is under sessionData.data
        console.log('[loadSessionFromData] ✅ Using NESTED session format (current save format)');
        battleState = sessionData.data.battleState;
        combatState = sessionData.data.combatState || {};
      } else if (sessionData.battleState) {
        // New format (v2.0+) with explicit battleState and combatState at root level
        console.log('[loadSessionFromData] ✅ Using NEW session format (v2.0+ root level)');
        battleState = sessionData.battleState;
        combatState = sessionData.combatState || {};
      } else if (sessionData.tokens) {
        // Legacy format (v1.x) - session data contains tokens directly
        console.warn('[loadSessionFromData] ⚠️ Using LEGACY session format (v1.x), converting to new format');
        battleState = {
          tokens: sessionData.tokens,
          mapImage: sessionData.mapImage,
          mapState: sessionData.mapState,
          mapImages: sessionData.mapImages,
          selectedTokenId: sessionData.selectedTokenId,
          showGrid: sessionData.showGrid,
          snapToGrid: sessionData.snapToGrid,
          gridSize: sessionData.gridSize,
          zoom: sessionData.zoom
        };
        combatState = {
          currentCombatantId: sessionData.currentCombatantId,
          combatStarted: sessionData.combatStarted,
          combatRound: sessionData.combatRound,
          combatTurn: sessionData.combatTurn
        };
      } else {
        console.error('[loadSessionFromData] ❌ UNRECOGNIZED SESSION FORMAT:');
        console.error('[loadSessionFromData] Available keys:', Object.keys(sessionData));
        console.error('[loadSessionFromData] Full session data:', sessionData);
        throw new Error(`Invalid session data format - missing battleState and tokens. Available keys: ${Object.keys(sessionData).join(', ')}`);
      }

      // Use the extracted/converted state data
      const state = battleState;

      // @date_of_change: 2025-09-26 - Removed manual GM setting - now derived from WebSocket context or single-player mode
      console.log('[loadSessionFromData] 🎯 User GM status determined by WebSocket or single-player mode');

      // @date_of_change 2025-09-15 - CRITICAL ENHANCEMENT: Complete combat state restoration
      console.log('[loadSessionFromData] 🔄 RESTORING ENHANCED SESSION DATA:');
      console.log('[loadSessionFromData] Session version:', sessionData.version);
      console.log('[loadSessionFromData] Combat data present:', !!combatState);
      console.log('[loadSessionFromData] Combat started:', combatState.combatStarted);
      console.log('[loadSessionFromData] Combat log entries:', combatState.combatLog?.combatLog?.length || 0);
      console.log('[loadSessionFromData] Initiative order entries:', combatState.initiativeOrder?.length || 0);
      
      // DEBUG: Log comprehensive token data from session data before loading
      if (state.tokens && state.tokens.length > 0) {
        console.log('[loadSessionFromData] Token data from session data:', 
          state.tokens.map(token => ({
            id: token.id,
            name: token.name,
            // Position data
            x: token.x,
            y: token.y,
            gridX: token.gridX,
            gridY: token.gridY,
            type: token.type,
            layer: token.layer,
            // Combat stats
            initiative: token.initiative,
            hp: token.hp,
            maxHp: token.maxHp,
            hpObj: token.hpObj ? JSON.stringify(token.hpObj) : undefined,
            ac: token.ac,
            // Status/conditions
            conditions: token.conditions ? token.conditions.join(', ') : undefined,
            statusEffects: token.statusEffects ? Object.keys(token.statusEffects).join(', ') : undefined,
            // Spell tracking
            spellsTracking: token.spells ? 'present' : 'missing',
            // Resources tracking
            resourcesTracking: token.resources ? 'present' : 'missing'
          })));
      }
      
      // Restore map state - using correct variable names from current implementation
      if (state.mapImage !== undefined) setMapImage(normalizePersistedMapUrl(state.mapImage));
      if (state.mapState) setMapState(normalizePersistedMapState(state.mapState));
      if (state.mapImages) setMapImages(normalizePersistedMapImages(state.mapImages));
      if (Array.isArray(state.sceneAssets)) setSceneAssets(state.sceneAssets);
      
      let restoredTokens = Array.isArray(state.tokens) ? state.tokens : [];

      // Restore token state
      if (state.tokens) {
        // date_of_change: 2026-05-19 - GAP-001: Also probe sessionData.data.* for nested format.
        const characterDataMap = (
          sessionData.snapshotTemplates
          || sessionData.characterTemplates
          || sessionData.data?.snapshotTemplates
          || sessionData.data?.characterTemplates
          || {}
        );
        const localCharacterEntities = UnifiedDataManager.getAllFromLocalStorage('character');
        const localMonsterEntities = UnifiedDataManager.getAllFromLocalStorage('monster');
        console.log('[loadSessionFromData] 🔍 Snapshot templates available:', Object.keys(characterDataMap));

        const rehydratedTokens = restoreSnapshotTokenCollection({
          tokens: state.tokens,
          templateMap: characterDataMap,
          canonicalCharacterEntities: localCharacterEntities,
          canonicalMonsterEntities: localMonsterEntities
        });

        // date_of_change: 2026-05-19 - GAP-001: This pass must NOT re-overwrite hp/ac/initiative/
        // abilities from raw snapshot fields. The rehydrator has already resolved those from
        // canonical data + runtime overlay. This pass is ONLY for:
        //   1. Coordinate coercion (x/y must be numbers for Konva)
        //   2. hpObj synthesis when the rehydrator left hp as a plain number
        //   3. Stripping internal snapshot ref keys
        //   4. Flagging legacy monster tokens that need async data reload
        // rule_reference: BATTLE_ROOM_REFACTOR_PLAN_01 §5.2 — creation and restoration must be mirrors.
        const tokensWithVerifiedData = rehydratedTokens.map(token => {
          const sourceToken = JSON.parse(JSON.stringify(token));
          const verifiedToken = {
            ...sourceToken,
            x: Number(sourceToken.x) || 0,
            y: Number(sourceToken.y) || 0,
            gridX: sourceToken.gridX !== undefined ? sourceToken.gridX : 0,
            gridY: sourceToken.gridY !== undefined ? sourceToken.gridY : 0,
            // Preserve rehydrated combat stats — do NOT re-coerce from raw fields here.
            // The rehydrator already resolved canonical values and applied runtime overlays.
            characterDataRef: undefined,
            monsterDataRef: undefined
          };

          // Ensure hpObj is always a well-formed object for HPFormatBridge consumers.
          // If the rehydrator produced numeric hp/maxHp without hpObj, synthesize it.
          if (!verifiedToken.hpObj || typeof verifiedToken.hpObj !== 'object') {
            if (typeof verifiedToken.hp === 'number' || typeof verifiedToken.maxHp === 'number') {
              verifiedToken.hpObj = {
                current: Number(verifiedToken.hp) || 0,
                max: Number(verifiedToken.maxHp) || 0
              };
            } else if (sourceToken.hpObj && typeof sourceToken.hpObj === 'object') {
              verifiedToken.hpObj = {
                current: Number(sourceToken.hpObj.current) || 0,
                max: Number(sourceToken.hpObj.max) || 0
              };
              verifiedToken.hp = verifiedToken.hpObj.current;
              verifiedToken.maxHp = verifiedToken.hpObj.max;
            }
          }

          if (verifiedToken.type === 'monster' && !verifiedToken.monsterData && !verifiedToken.entityPayload) {
            console.log(`[loadSessionFromData] 🔄 Attempting to reload monster data for legacy session: ${verifiedToken.name}`);
            verifiedToken._needsMonsterDataReload = true;
            verifiedToken._monsterNameForReload = verifiedToken.name.toLowerCase().replace(/\s+/g, '-');
          }

          return verifiedToken;
        });

        restoredTokens = tokensWithVerifiedData;
        setTokens(tokensWithVerifiedData);
      }

      // Restore grid settings
      if (state.showGrid !== undefined) setShowGrid(state.showGrid);
      if (state.snapToGrid !== undefined) setSnapToGrid(state.snapToGrid);
      if (state.gridSize !== undefined) setGridSize(normalizeBattleRoomGridSize(state.gridSize, 50));
      if (state.gridOpacity !== undefined) setGridOpacity(state.gridOpacity);
      if (state.gridWidth !== undefined) setGridWidth(normalizeGridDimensionValue(state.gridWidth, 40) ?? 40);
      if (state.gridHeight !== undefined) setGridHeight(normalizeGridDimensionValue(state.gridHeight, 30) ?? 30);
      if (state.zoom !== undefined) setZoom(normalizeBattleRoomZoom(state.zoom, 1));
      // Restore fog-of-war settings
      // date_of_change: 2025-09-24 - Restore fog-of-war state including operations
      if (state.fogOfWar !== undefined) setFogOfWar(state.fogOfWar);

      // GAP-002: Restore drawing layer strokes from session
      // date_of_change: 2026-05-20
      if (Array.isArray(state.drawings)) setDrawings(state.drawings);

      // Restore layer settings
        if (state.activeLayer !== undefined) setActiveLayer(normalizeBattleLayerId(state.activeLayer, 'map'));
      if (state.layers !== undefined) setLayers(state.layers);
      
      // Restore selection state
      if (state.selectedTokenId !== undefined) {
        setSelectedTokenId(state.selectedTokenId);
      } else {
        // Deselect everything for safety
        setSelectedTokenId(null);
      }

      // @date_of_change 2025-09-15 - CRITICAL ENHANCEMENT: Combat state restoration
      // Restore combat systems after all basic state is restored
      if (combatState && Object.keys(combatState).length > 0) {
        console.log('[loadSessionFromData] 🔄 RESTORING COMBAT STATE:');

        // Restore CombatLogManager state using proper import method
        if (combatState.combatLog && CombatLogManager) {
          try {
            CombatLogManager.importState(combatState.combatLog);
            console.log('[loadSessionFromData] ✅ CombatLogManager state restored successfully');
          } catch (error) {
            console.error('[loadSessionFromData] ❌ Failed to restore CombatLogManager state:', error);
          }
        } else if (CombatLogManager) {
          CombatLogManager.importState({});
        }

        // Restore ActionEconomyManager state using proper import method
        if (combatState.actionEconomy && ActionEconomyManager) {
          try {
            ActionEconomyManager.importState(combatState.actionEconomy);
            console.log('[loadSessionFromData] ✅ ActionEconomyManager state restored successfully');
          } catch (error) {
            console.error('[loadSessionFromData] ❌ Failed to restore ActionEconomyManager state:', error);
          }
        } else if (ActionEconomyManager) {
          ActionEconomyManager.resetAllStates();
        }

        // Log comprehensive combat restoration summary
        console.log('[loadSessionFromData] 🎯 COMBAT RESTORATION COMPLETE:', {
          combatStarted: combatState.combatStarted,
          currentRound: combatState.currentRound,
          currentTurn: combatState.currentTurn,
          currentCombatantId: combatState.currentCombatantId,
          initiativeOrderCount: combatState.initiativeOrder?.length || 0
        });
      } else {
        console.log('[loadSessionFromData] ℹ️ No combat state to restore (legacy session or pre-combat)');
        // Loading a legacy/pre-combat snapshot is still an authoritative
        // replacement. Clear singleton runtime left by the abandoned room so
        // it cannot leak turn history or action availability into this save.
        // date_of_change: 2026-08-20
        CombatLogManager?.importState({});
        ActionEconomyManager?.resetAllStates();
      }

      // @date_of_change 2025-09-16 - POST-LOAD MONSTER DATA RESTORATION
      // Restore monster data for tokens that were marked for reloading
      const tokensNeedingMonsterData = restoredTokens.filter(token => token._needsMonsterDataReload);
      if (tokensNeedingMonsterData.length > 0) {
        console.log(`[loadSessionFromData] 🔄 Post-load monster data restoration for ${tokensNeedingMonsterData.length} tokens`);

        // Load monster data asynchronously without blocking session load
        setTimeout(async () => {
          const DataService = require('../../services/DataService').default;

          for (const token of tokensNeedingMonsterData) {
            try {
              console.log(`[loadSessionFromData] 🔄 Reloading monster data for: ${token.name}`);
              const reloadedMonsterData = await DataService.getMonsterData(token._monsterNameForReload);

              if (reloadedMonsterData) {
                console.log(`[loadSessionFromData] ✅ Successfully reloaded monster data for: ${token.name}`, {
                  hasActions: !!reloadedMonsterData.actions,
                  actionsCount: reloadedMonsterData.actions?.length || 0
                });

                // Update the token with reloaded monster data
                setTokens(prevTokens => prevTokens.map(prevToken => {
                  if (prevToken.id === token.id) {
                    return {
                      ...prevToken,
                      monsterData: reloadedMonsterData,
                      actions: reloadedMonsterData.actions || prevToken.actions,
                      legendaryActions: reloadedMonsterData.legendaryActions || prevToken.legendaryActions,
                      specialAbilities: reloadedMonsterData.specialAbilities || prevToken.specialAbilities,
                      // Remove the reload flags
                      _needsMonsterDataReload: undefined,
                      _monsterNameForReload: undefined
                    };
                  }
                  return prevToken;
                }));
              } else {
                console.log(`[loadSessionFromData] ⚠️ Could not reload monster data for: ${token.name}`);
              }
            } catch (error) {
              console.log(`[loadSessionFromData] ❌ Error reloading monster data for ${token.name}:`, error);
            }
          }
        }, 100); // Small delay to allow session load to complete first
      }

      console.log('[loadSessionFromData] Session loaded successfully');
      // Defer re-enabling effects to the next tick so initial effects can skip
      setTimeout(() => { isLoadingSessionRef.current = false; }, 0);

      // @date_of_change 2025-09-15 - Return BattleRoom state for component restoration
      return {
        success: true,
        battleRoomState: combatState.battleRoomState || null
      };
    } catch (error) {
      console.error('[loadSessionFromData] Error loading session data:', error);
      isLoadingSessionRef.current = false;
      lastLoadedSessionSignatureRef.current = null;
      throw error;
    }
  }, [setTokens, setMapImage, setMapState, setMapImages, setSceneAssets,
      setShowGrid, setSnapToGrid, setGridSize, setGridOpacity, setGridWidth, setGridHeight, 
      setZoom, setActiveLayer, setLayers, setSelectedTokenId, normalizeGridDimensionValue]);

  /**
   * Session load handler - Loads from local storage OR file import
   * @param {string} sessionId - ID of session to load from storage (optional)
   * @param {File|Event} fileOrEvent - File object or event from file input (optional)
   */
  const handleLoadSession = useCallback((sessionId = null, fileOrEvent = null) => {
    try {
      console.log('[handleLoadSession] Starting load session process', { sessionId, hasFile: !!fileOrEvent });
      
      // If we have a session ID, load from storage (now returns Promise for file-based loading)
      // @date_of_change 2025-09-15 - FILE-BASED SESSION STORAGE
      if (sessionId) {
        return new Promise((resolve, reject) => {
          SessionManager.loadSession(sessionId)
            .then(sessionData => {
              if (!sessionData) {
                reject(new Error(`Session with ID ${sessionId} not found`));
                return;
              }

              // Load data into state
              // A visible user-initiated Load is an authoritative restore even
              // when this is the same save selected earlier. The duplicate guard
              // exists only for repeated automatic route/service effects.
              // date_of_change: 2026-08-20
              const result = loadSessionFromData(sessionData, { forceRestore: true });
              resolve({
                ...result,
                sessionData
              });
            })
            .catch(error => {
              console.error('[handleLoadSession] Failed to load session file:', error);
              reject(error);
            });
        });
      }
      
      // If we have a file, load from file
      if (fileOrEvent) {
        // Handle both File objects and event objects
        const file = fileOrEvent.target ? fileOrEvent.target.files[0] : fileOrEvent;
        
        if (!file) {
          throw new Error('No file provided for session load');
        }
        
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          
          reader.onload = (e) => {
            try {
              const fileContent = e.target.result;
              const sessionData = JSON.parse(fileContent);

              console.log('[handleLoadSession] 🔍 DEBUGGING: Loaded file structure:', {
                hasDirectBattleState: !!sessionData.battleState,
                hasDataBattleState: !!(sessionData.data && sessionData.data.battleState),
                topLevelKeys: Object.keys(sessionData),
                dataKeys: sessionData.data ? Object.keys(sessionData.data) : null
              });

              // @date_of_change 2025-09-17 - HANDLE MULTIPLE SESSION FILE FORMATS
              // Extract battleState data from different possible locations
              let battleStateData = null;

              if (sessionData.battleState) {
                // Direct battleState (legacy format)
                console.log('[handleLoadSession] Using direct battleState format');
                battleStateData = sessionData;
              } else if (sessionData.data && sessionData.data.battleState) {
                // Nested under data (new format from production file storage)
                console.log('[handleLoadSession] Using nested data.battleState format');
                battleStateData = sessionData.data;
              } else {
                throw new Error('Invalid session file: No battleState found');
              }

              // Load data into state
              const result = loadSessionFromData(battleStateData, { forceRestore: true });
              resolve({
                ...result,
                sessionData: battleStateData
              });
            } catch (error) {
              console.error('[handleLoadSession] Error parsing file:', error);
              reject(error);
            }
          };
          
          reader.onerror = (error) => {
            console.error('[handleLoadSession] FileReader error:', error);
            reject(new Error(`Failed to read file: ${error.message || 'Unknown error'}`));
          };
          
          reader.readAsText(file);
        });
      }
      
      throw new Error('Either sessionId or file must be provided');
    } catch (error) {
      console.error('[handleLoadSession] Failed to load session:', error);

      // Show appropriate error message based on error type
      if (error.message?.includes('not found')) {
        alert('Session not found. It may have been deleted or expired.');
      } else if (error.message?.includes('parse')) {
        alert('Invalid session file format. Please select a valid session file.');
      } else {
        alert(`Failed to load session: ${error.message || 'Unknown error'}`);
      }

      return { success: false, battleRoomState: null };
    }
  }, [loadSessionFromData]);

  // Note: All session management functionality is defined earlier in the file

  /**
   * All session save/load functionality is fully implemented earlier in this file.
   * See handleSaveSession, loadSessionFromData, and handleLoadSession functions above.
   */

  /**
   * Handle dropping assets onto the battle map
   * @param {Object} event - Drop event
   * @param {Object} position - Position {x, y} where the drop occurred
   */
  const handleDrop = useCallback((event, position) => {
    if (!event || !position) {
      console.error('[handleDrop] Invalid drop event or position');
      return false;
    }

    try {
      // Get the dropped data
      const data = event.dataTransfer?.getData('application/json');
      if (!data) {
        console.error('[handleDrop] No valid data in drop event');
        return false;
      }

      const assetData = JSON.parse(data);
      console.log('[handleDrop] Dropped asset data:', assetData);

      // Process based on asset type
      if (assetData.type === 'map') {
        // Handle map drops
        if (assetData.url) {
          handleMapUpload(assetData.url, position);
          return true;
        }
      } else if (assetData.type === 'token' || assetData.type === 'player' || assetData.type === 'monster') {
        console.error('[handleDrop] Raw image token drops are retired. Use the BattleRoom scene-asset placement workflow.');
        return false;
      }

      console.error('[handleDrop] Unsupported asset type or missing URL');
      return false;
    } catch (error) {
      console.error('[handleDrop] Error processing drop event:', error);
      return false;
    }
  }, [handleMapUpload]);

  /**
   * Handle grid opacity change
   * @param {number} opacity - New grid opacity (0-1)
   */
  const handleGridOpacityChange = useCallback((opacity) => {
    const parsed = parseFloat(opacity);
    const validOpacity = Math.min(Math.max(isNaN(parsed) ? 0.5 : parsed, 0), 1);
    // Guard: skip if unchanged to avoid unnecessary renders
    if (gridOpacity === validOpacity) {
      console.log('[handleGridOpacityChange] No change detected; skipping state update');
      return;
    }
    setGridOpacity(validOpacity);
    // Save grid opacity to localStorage for persistence between renders
    localStorage.setItem('battleRoomGridOpacity', validOpacity);
    console.log(`[handleGridOpacityChange] Grid opacity set to ${validOpacity} and saved to localStorage`);
  }, [gridOpacity]);

  /**
   * Handle grid size change
   * @param {number} newSize - New grid size in pixels
   */
  const handleGridSizeChange = useCallback((newSize) => {
    const validSize = normalizeBattleRoomGridSize(newSize, 50);
    // Guard: skip if unchanged to avoid unnecessary renders
    if (gridSize === validSize) {
      console.log('[handleGridSizeChange] No change detected; skipping state update');
      return;
    }
    setGridSize(validSize);
    // Save grid size to localStorage for persistence between renders
    localStorage.setItem('battleRoomGridSize', validSize);
    console.log(`[handleGridSizeChange] Grid size set to ${validSize}px and saved to localStorage`);

    // @date_of_change: 2025-10-03 - Sync grid size changes to multiplayer
    syncStateToMultiplayer('map_update', { gridSize: validSize });
  }, [gridSize, syncStateToMultiplayer]);

  // Handle grid width change (columns)
  const handleGridWidthChange = useCallback((width) => {
    const normalized = normalizeGridDimensionValue(width, 40);
    if (normalized === null) {
      console.log('[handleGridWidthChange] Ignoring NaN/empty input');
      return;
    }
    if (gridWidth === normalized) {
      return;
    }
    setGridWidth(normalized);
    localStorage.setItem('battleRoomGridWidth', normalized);
    syncStateToMultiplayer('map_update', { gridWidth: normalized });
  }, [gridWidth, normalizeGridDimensionValue, syncStateToMultiplayer]); // @date_of_change: 2025-11-04 - Added normalizeGridDimensionValue dependency per exhaustive-deps

  // Handle grid height change (rows)
  const handleGridHeightChange = useCallback((height) => {
    const normalized = normalizeGridDimensionValue(height, 30);
    if (normalized === null) {
      console.log('[handleGridHeightChange] Ignoring NaN/empty input');
      return;
    }
    if (gridHeight === normalized) {
      return;
    }
    setGridHeight(normalized);
    localStorage.setItem('battleRoomGridHeight', normalized);
    syncStateToMultiplayer('map_update', { gridHeight: normalized });
  }, [gridHeight, normalizeGridDimensionValue, syncStateToMultiplayer]); // @date_of_change: 2025-11-04 - Added normalizeGridDimensionValue dependency per exhaustive-deps

  // Handle token layer change functionality
  const handleTokenLayerChange = useCallback((tokenId, newLayer) => {
    if (!tokenId || !newLayer) {
      console.error('[handleTokenLayerChange] Invalid token ID or layer');
      return false;
    }

    const tokenIndex = tokens.findIndex(token => token.id === tokenId);
    if (tokenIndex === -1) {
      console.error(`[handleTokenLayerChange] Token with ID ${tokenId} not found`);
      return false;
    }

    const currentToken = tokens[tokenIndex];
    const normalizedCurrentLayer = normalizeBattleLayerId(currentToken.layer, 'token');
    const normalizedNewLayer = normalizeBattleLayerId(newLayer);

    if (normalizedCurrentLayer === normalizedNewLayer) {
      console.log(`[handleTokenLayerChange] Token ${tokenId} already on layer ${normalizedNewLayer}, no update needed`);
      return true;
    }

    const newProps = { layer: normalizedNewLayer };
    // Adjust token type based on previous type when moving off map layer
    if (currentToken.type === 'map') {
      // Default to 'player' type when coming from map layer
      newProps.type = currentToken.originalType || 'player';
    } else {
      // Store original type for potential layer changes
      newProps.originalType = currentToken.type;
    }

    const result = handleTokenUpdate(tokenId, newProps);
    if (result) {
      console.log(`[handleTokenLayerChange] Token ${tokenId} moved to ${normalizedNewLayer} layer with type: ${newProps.type ?? currentToken.type}`);
    }
    return result;
  }, [tokens, handleTokenUpdate]);

  return {
    tokens,
    setTokens,
    selectedTokenId,
    setSelectedTokenId,
    mapImage,
    setMapImage,
    mapImages, // Layer-aware map state
    setMapImages, // Layer-aware map state setter
    sceneAssets,
    setSceneAssets,
    mapState, // Interactive map state (position, size, selection)
    setMapState, // Interactive map state setter
    stageRef,
    handleMapUpload,
    handleTokenUpload,
    handleDragEnd,
    handleDrop,
    handleTokenUpdate, // Unified token update handler
    handleBatchTokenUpdates, // Atomic multi-token update handler (group moves)
    updateTokenPosition, // Grid-aware position update handler
    isWithinGridBounds, // Check if coordinates are within grid bounds
    getGridCoordinates, // Get grid coordinates with bounds checking
    // Grid state and handlers
    showGrid,
    setShowGrid,
    snapToGrid,
    setSnapToGrid,
    gridSize,
    setGridSize,
    // @date_of_change: 2025-10-13 - Expose grid context for AoE and range math
    gridType,
    setGridType,
    feetPerCell,
    setFeetPerCell,
    gridOpacity,
    setGridOpacity,
    gridWidth,
    setGridWidth,
    gridHeight,
    setGridHeight,
    zoom,
    setZoom,
    handleGridToggle,
    handleSnapToggle,
    handleGridSizeChange,
    handleGridOpacityChange,
    handleGridWidthChange,
    handleGridHeightChange,
    // Combat handlers
    handleExecuteAttack,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleSaveSession,
    handleLoadSession,
    loadSessionFromData,
    snapToGridPosition,
    // Layer management utilities
    activeLayer,
    setActiveLayer,
    layers,
    setLayers,
    isLayerInteractive,
    getAssetDraggable,
    // Map interaction handlers
    handleMapSelect,
    handleMapChange,
    handleMapDragEnd,
    handleDeselectAll,
    handleTokenSelect,
    handleTokenDelete,
    handleMapDelete,
    // Token creation utilities
    createTokenFromDashboardData,
    // Token autoscaling utilities
    getTokenSizeFromCategory,
    handleTokenSizeChange,
    
    // Token management functions
    handleTokenLayerChange, // New handler for moving tokens between layers
    // Token property management is now unified through handleTokenUpdate
    // All other handlers defined as useCallback functions above

    // Multiplayer functions
    // date_of_change: 2025-09-24 - Added multiplayer session management
    isMultiplayerMode,
    multiplayerSessionId,
    sessionParticipants,
    sessionStatus,
    isConnected: isConnected,
    enableMultiplayerMode,
    disableMultiplayerMode,
    syncStateToMultiplayer,
    seedCurrentStateToMultiplayer,
    seedSessionDataToMultiplayer,
    waitForAuthoritativeRevision,
    pendingAuthoritativeProjectionCount,

    // Combat action synchronization functions (1s delay tolerance)
    // date_of_change: 2025-09-24 - Added real-time combat sync
    sendCombatAction,
    sendInitiativeUpdate,
    sendTokenUpdate,

    // Token ownership and control functions
    // date_of_change: 2025-09-24 - Added token permission system
    // @date_of_change: 2025-09-26 - Removed setIsGameMaster - isGameMaster now derived from WebSocket context
    isGameMaster,
    connectedPlayers,
    setConnectedPlayers,
    tokenOwnership,
    canControlToken,
    assignTokenControl,
    unassignTokenControl,
    getTokenControlInfo,

    // Fog of War API
    // date_of_change: 2025-09-24 - Expose fog controls to UI
    fogOfWar,
    toggleFogEnabled,
    setFogOpacity,
    toggleFogIgnoreGM,
    addFogOperationRectNormalized,
    addFogOperationPolygon, // GAP-003: polygon/lasso fog ops — date_of_change: 2026-05-20
    undoLastFogOperation,   // GAP-003: undo last fog operation — date_of_change: 2026-05-20
    clearFogOperations,
    revealAllFog,
    coverAllFog,

    // GAP-002: Drawing layer API
    // date_of_change: 2026-05-20
    drawings,
    drawingEnabled,
    setDrawingEnabled,
    drawingTool,
    setDrawingTool,
    drawingColor,
    setDrawingColor,
    drawingLineWidth,
    setDrawingLineWidth,
    addDrawing,
    undoDrawing,
    clearDrawings
  };
};

export default useBattleRoom;
